import { useEffect, useState, useMemo, useRef, useContext, createContext } from 'react';
import { formatCents } from '@/utils/cn';
import { apiClient, api } from '@/services/api';

/**
 * Renderiza um slide para pré-visualização web (espelha o player do TV).
 * Usa produtos REAIS do tenant quando disponíveis; mock como fallback.
 */

export interface RealProduct {
  codigo: string;
  descricao: string;
  preco_cents: number;
  preco_promo_cents?: number | null;
  categoria?: string | null;
  departamento?: string | null;
  imagem_url?: string | null;
  em_destaque?: boolean;
}

const MOCK_PRODUCTS_BY_CATEGORY: Record<string, any[]> = {
  'Carnes Bovinas': [
    { codigo: 'BOV01', descricao: 'Picanha Premium', preco_cents: 8990, preco_promo_cents: 7490, emoji: '🥩' },
    { codigo: 'BOV02', descricao: 'Filé Mignon', preco_cents: 9990, emoji: '🥩' },
    { codigo: 'BOV03', descricao: 'Alcatra', preco_cents: 4990, preco_promo_cents: 3990, emoji: '🥩' },
    { codigo: 'BOV04', descricao: 'Costela', preco_cents: 3490, emoji: '🥩' },
    { codigo: 'BOV05', descricao: 'Maminha', preco_cents: 5490, emoji: '🥩' },
    { codigo: 'BOV06', descricao: 'Contra-filé', preco_cents: 5990, preco_promo_cents: 4990, emoji: '🥩' },
  ],
  'Suínos e Aves': [
    { codigo: 'SUI01', descricao: 'Pernil Suíno', preco_cents: 2490, emoji: '🍖' },
    { codigo: 'SUI02', descricao: 'Lombo Suíno', preco_cents: 2990, preco_promo_cents: 2390, emoji: '🍖' },
    { codigo: 'AVE01', descricao: 'Frango Inteiro', preco_cents: 1490, emoji: '🍗' },
    { codigo: 'AVE02', descricao: 'Coxa de Frango', preco_cents: 1290, preco_promo_cents: 990, emoji: '🍗' },
    { codigo: 'AVE03', descricao: 'Peito de Frango', preco_cents: 1890, emoji: '🍗' },
    { codigo: 'AVE04', descricao: 'Asa de Frango', preco_cents: 990, emoji: '🍗' },
    { codigo: 'SUI03', descricao: 'Costelinha', preco_cents: 2790, emoji: '🍖' },
    { codigo: 'SUI04', descricao: 'Bacon', preco_cents: 3490, emoji: '🥓' },
  ],
  'Defumados': [
    { codigo: 'DEF01', descricao: 'Linguiça Calabresa', preco_cents: 2290, emoji: '🌭' },
    { codigo: 'DEF02', descricao: 'Linguiça Toscana', preco_cents: 1990, preco_promo_cents: 1590, emoji: '🌭' },
    { codigo: 'DEF03', descricao: 'Linguiça Pernil', preco_cents: 2490, emoji: '🌭' },
    { codigo: 'DEF04', descricao: 'Salame Italiano', preco_cents: 4990, emoji: '🥓' },
    { codigo: 'DEF05', descricao: 'Mortadela', preco_cents: 1490, emoji: '🥩' },
    { codigo: 'DEF06', descricao: 'Presunto', preco_cents: 2990, emoji: '🥓' },
  ],
  'Pães': [
    { codigo: 'PAO01', descricao: 'Pão Francês (kg)', preco_cents: 1490, emoji: '🥖' },
    { codigo: 'PAO02', descricao: 'Pão Italiano', preco_cents: 1290, emoji: '🍞' },
    { codigo: 'PAO03', descricao: 'Pão de Queijo (kg)', preco_cents: 4990, emoji: '🧀' },
    { codigo: 'PAO04', descricao: 'Croissant', preco_cents: 690, emoji: '🥐' },
    { codigo: 'PAO05', descricao: 'Pão Doce', preco_cents: 590, emoji: '🍞' },
    { codigo: 'PAO06', descricao: 'Rosquinha', preco_cents: 390, emoji: '🍩' },
  ],
  'Bolos': [
    { codigo: 'BOL01', descricao: 'Bolo de Chocolate', preco_cents: 4990, emoji: '🍰' },
    { codigo: 'BOL02', descricao: 'Bolo de Cenoura', preco_cents: 3990, preco_promo_cents: 2990, emoji: '🍰' },
    { codigo: 'BOL03', descricao: 'Torta de Limão', preco_cents: 5990, emoji: '🥧' },
    { codigo: 'BOL04', descricao: 'Cheesecake', preco_cents: 6990, emoji: '🍰' },
    { codigo: 'BOL05', descricao: 'Bolo Red Velvet', preco_cents: 7990, emoji: '🍰' },
    { codigo: 'BOL06', descricao: 'Brownie', preco_cents: 1290, emoji: '🍫' },
  ],
  'Hortifruti': [
    { codigo: 'HOR01', descricao: 'Banana Prata (kg)', preco_cents: 599, emoji: '🍌' },
    { codigo: 'HOR02', descricao: 'Maçã Fuji (kg)', preco_cents: 999, emoji: '🍎' },
    { codigo: 'HOR03', descricao: 'Tomate (kg)', preco_cents: 799, preco_promo_cents: 599, emoji: '🍅' },
    { codigo: 'HOR04', descricao: 'Cenoura (kg)', preco_cents: 499, emoji: '🥕' },
    { codigo: 'HOR05', descricao: 'Batata (kg)', preco_cents: 599, emoji: '🥔' },
    { codigo: 'HOR06', descricao: 'Cebola (kg)', preco_cents: 499, emoji: '🧅' },
    { codigo: 'HOR07', descricao: 'Abacaxi (un)', preco_cents: 999, emoji: '🍍' },
    { codigo: 'HOR08', descricao: 'Manga (kg)', preco_cents: 799, emoji: '🥭' },
  ],
  'Frutas': [
    { codigo: 'FRU01', descricao: 'Banana (kg)', preco_cents: 599, emoji: '🍌' },
    { codigo: 'FRU02', descricao: 'Maçã (kg)', preco_cents: 999, emoji: '🍎' },
    { codigo: 'FRU03', descricao: 'Laranja (kg)', preco_cents: 399, emoji: '🍊' },
    { codigo: 'FRU04', descricao: 'Uva (kg)', preco_cents: 1499, emoji: '🍇' },
    { codigo: 'FRU05', descricao: 'Mamão (un)', preco_cents: 599, emoji: '🥭' },
    { codigo: 'FRU06', descricao: 'Abacaxi (un)', preco_cents: 999, emoji: '🍍' },
    { codigo: 'FRU07', descricao: 'Morango (cx)', preco_cents: 1290, preco_promo_cents: 990, emoji: '🍓' },
    { codigo: 'FRU08', descricao: 'Melancia (kg)', preco_cents: 299, emoji: '🍉' },
  ],
  'Verduras': [
    { codigo: 'VER01', descricao: 'Alface (un)', preco_cents: 299, emoji: '🥬' },
    { codigo: 'VER02', descricao: 'Couve (mç)', preco_cents: 399, emoji: '🥬' },
    { codigo: 'VER03', descricao: 'Espinafre (mç)', preco_cents: 499, emoji: '🥬' },
    { codigo: 'VER04', descricao: 'Brócolis (un)', preco_cents: 599, emoji: '🥦' },
    { codigo: 'VER05', descricao: 'Rúcula (mç)', preco_cents: 399, emoji: '🥬' },
    { codigo: 'VER06', descricao: 'Agrião (mç)', preco_cents: 299, emoji: '🥬' },
  ],
  'Mercearia': [
    { codigo: 'MER01', descricao: 'Arroz Tipo 1 (5kg)', preco_cents: 2999, preco_promo_cents: 2490, emoji: '🍚' },
    { codigo: 'MER02', descricao: 'Feijão (1kg)', preco_cents: 899, emoji: '🫘' },
    { codigo: 'MER03', descricao: 'Açúcar (1kg)', preco_cents: 499, emoji: '🍬' },
    { codigo: 'MER04', descricao: 'Café (500g)', preco_cents: 1990, emoji: '☕' },
    { codigo: 'MER05', descricao: 'Óleo (900ml)', preco_cents: 799, preco_promo_cents: 599, emoji: '🫒' },
    { codigo: 'MER06', descricao: 'Macarrão (500g)', preco_cents: 549, emoji: '🍝' },
    { codigo: 'MER07', descricao: 'Leite (1L)', preco_cents: 599, emoji: '🥛' },
    { codigo: 'MER08', descricao: 'Farinha (1kg)', preco_cents: 449, emoji: '🌾' },
  ],
  'Bebidas': [
    { codigo: 'BEB01', descricao: 'Refrigerante 2L', preco_cents: 999, preco_promo_cents: 799, emoji: '🥤' },
    { codigo: 'BEB02', descricao: 'Cerveja Long Neck', preco_cents: 499, emoji: '🍺' },
    { codigo: 'BEB03', descricao: 'Suco Natural', preco_cents: 899, emoji: '🧃' },
    { codigo: 'BEB04', descricao: 'Água Mineral', preco_cents: 299, emoji: '💧' },
    { codigo: 'BEB05', descricao: 'Energético', preco_cents: 1290, emoji: '⚡' },
    { codigo: 'BEB06', descricao: 'Vinho Tinto', preco_cents: 4990, preco_promo_cents: 3990, emoji: '🍷' },
  ],
  'Genéricos': [
    { codigo: 'MED01', descricao: 'Dipirona 500mg', preco_cents: 999, emoji: '💊' },
    { codigo: 'MED02', descricao: 'Paracetamol', preco_cents: 1290, emoji: '💊' },
    { codigo: 'MED03', descricao: 'Ibuprofeno', preco_cents: 1490, emoji: '💊' },
    { codigo: 'MED04', descricao: 'Vitamina C', preco_cents: 1990, emoji: '💊' },
    { codigo: 'MED05', descricao: 'Antialérgico', preco_cents: 2490, emoji: '💊' },
    { codigo: 'MED06', descricao: 'Anti-inflamatório', preco_cents: 1890, emoji: '💊' },
  ],
  'Higiene': [
    { codigo: 'HIG01', descricao: 'Shampoo 400ml', preco_cents: 1990, emoji: '🧴' },
    { codigo: 'HIG02', descricao: 'Sabonete', preco_cents: 399, preco_promo_cents: 299, emoji: '🧼' },
    { codigo: 'HIG03', descricao: 'Pasta de Dente', preco_cents: 990, emoji: '🪥' },
    { codigo: 'HIG04', descricao: 'Desodorante', preco_cents: 1490, emoji: '🧴' },
    { codigo: 'HIG05', descricao: 'Papel Higiênico (4)', preco_cents: 999, emoji: '🧻' },
    { codigo: 'HIG06', descricao: 'Sabão em Pó (1kg)', preco_cents: 1290, emoji: '🧺' },
    { codigo: 'HIG07', descricao: 'Detergente', preco_cents: 299, emoji: '🧽' },
    { codigo: 'HIG08', descricao: 'Amaciante', preco_cents: 999, emoji: '🌸' },
  ],
};

/**
 * Escolhe produtos para renderizar.
 * Prioridade: produtos REAIS do tenant filtrados → fallback pra mock.
 */
function normalizeCat(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
}

/**
 * Match flexível por categoria. Estratégia em ordem decrescente de força:
 * 1. Equivalência exata após normalize.
 * 2. Stems iguais (remove plurais e variações de gênero a/o no final de cada token).
 *    Pega "Carnes Bovinas" ⇄ "Carne Bovino" mas NÃO pega "Carne Bovina" ⇄ "Carne Suína"
 *    porque os stems do segundo token diferem (bovin × suin).
 */
function categoryMatches(productCat: string, filterCat: string): boolean {
  const a = normalizeCat(productCat);
  const b = normalizeCat(filterCat);
  if (!a || !b) return false;
  if (a === b) return true;
  const sa = stem(a), sb = stem(b);
  if (sa === sb) return true;
  // Último token do stem casa: "Bovinos" ⇄ "Carne Bovina" via "bovin"; "Frangos" ⇄ "Frango".
  const aLast = sa.split(' ').pop() ?? '';
  const bLast = sb.split(' ').pop() ?? '';
  return !!aLast && aLast.length >= 4 && aLast === bLast;
}

function stem(s: string): string {
  return s
    .split(' ')
    .map(t => t.replace(/(s|as|os|es)$/, '').replace(/(a|o|e)$/, ''))
    .filter(Boolean)
    .join(' ');
}

// preFiltered=true: os produtos já vieram filtrados do servidor (mesma query da TV),
// então NÃO refiltra por categoria/departamento/promo — só ordena, intercala e corta.
function pickProducts(filter: any, limit: number, onlyPromo: boolean, realProducts?: RealProduct[], preFiltered = false): any[] {
  const categoriasFiltro: string[] = Array.isArray(filter?.categorias)
    ? filter.categorias.filter((c: any) => typeof c === 'string' && c.trim()).map((c: string) => normalizeCat(c))
    : (filter?.categoria ? [normalizeCat(String(filter.categoria))] : []);
  const departamentosFiltro: string[] = Array.isArray(filter?.departamentos)
    ? filter.departamentos.filter((d: any) => typeof d === 'string' && d.trim()).map((d: string) => normalizeCat(d))
    : (filter?.departamento ? [normalizeCat(String(filter.departamento))] : []);

  // 1. Tenta produtos reais (filtrados por categoria/departamento se especificado)
  if (realProducts && realProducts.length > 0) {
    let pool = [...realProducts];
    if (!preFiltered) {
      if (categoriasFiltro.length > 0) {
        const filtered = pool.filter(p => p.categoria && categoriasFiltro.some(cat => categoryMatches(p.categoria!, cat)));
        if (filtered.length > 0) pool = filtered;
      }
      if (departamentosFiltro.length > 0) {
        const filtered = pool.filter(p => p.departamento && departamentosFiltro.some(dep => normalizeCat(p.departamento!) === dep));
        if (filtered.length > 0) pool = filtered;
      }
      if (onlyPromo) pool = pool.filter(p => p.preco_promo_cents);
    }
    if (pool.length > 0) {
      // Intercala produtos das diferentes categorias para garantir que todas apareçam mesmo com `limit` baixo.
      const sorted = pool.sort((a, b) => Number(!!b.em_destaque) - Number(!!a.em_destaque));
      const arranged = categoriasFiltro.length > 1
        ? interleaveByCategory(sorted, categoriasFiltro)
        : sorted;
      return arranged
        .slice(0, limit)
        .map(p => ({ ...p, emoji: p.imagem_url ? null : guessEmoji(p) }));
    }
  }

  // 2. Fallback para mock por categoria
  let pool: any[] = [];
  if (categoriasFiltro.length > 0) {
    const matchedKeys = Object.keys(MOCK_PRODUCTS_BY_CATEGORY).filter(k => categoriasFiltro.includes(normalizeCat(k)));
    if (matchedKeys.length > 0) {
      const buckets = matchedKeys.map(k => MOCK_PRODUCTS_BY_CATEGORY[k]);
      pool = interleaveBuckets(buckets);
    } else {
      pool = Object.values(MOCK_PRODUCTS_BY_CATEGORY).flat();
    }
  } else if (departamentosFiltro.length > 0) {
    pool = MOCK_PRODUCTS_BY_CATEGORY['Mercearia'] ?? [];
  } else {
    pool = Object.values(MOCK_PRODUCTS_BY_CATEGORY).flat();
  }
  if (onlyPromo) pool = pool.filter(p => p.preco_promo_cents);
  return pool.slice(0, limit);
}

/** Distribui produtos round-robin entre as categorias filtradas, garantindo que todas apareçam. */
function interleaveByCategory(products: any[], categoriasFiltro: string[]): any[] {
  // Atribui cada produto à PRIMEIRA categoria do filtro que faz match (evita duplicação quando match é fuzzy).
  const buckets: any[][] = categoriasFiltro.map(() => []);
  const used = new Set<any>();
  for (const p of products) {
    if (!p.categoria) continue;
    const idx = categoriasFiltro.findIndex(cat => categoryMatches(p.categoria, cat));
    if (idx >= 0 && !used.has(p)) {
      buckets[idx].push(p);
      used.add(p);
    }
  }
  return interleaveBuckets(buckets);
}

function interleaveBuckets(buckets: any[][]): any[] {
  const out: any[] = [];
  const maxLen = Math.max(0, ...buckets.map(b => b.length));
  for (let i = 0; i < maxLen; i++) {
    for (const b of buckets) if (i < b.length) out.push(b[i]);
  }
  return out;
}

/** Escolhe emoji baseado no nome/categoria quando produto real não tem imagem. */
function guessEmoji(p: RealProduct): string {
  const t = (p.descricao + ' ' + (p.categoria ?? '')).toLowerCase();
  if (/picanha|file|alcatra|costela|maminha|contra-file|carne|bovino/.test(t)) return '🥩';
  if (/frango|ave|peito|coxa|asa/.test(t)) return '🍗';
  if (/su[ií]no|porco|pernil|lombo|bacon|costelinha/.test(t)) return '🍖';
  if (/lingui[çc]a|salame/.test(t)) return '🌭';
  if (/p[ãa]o|baguete/.test(t)) return '🥖';
  if (/croissant/.test(t)) return '🥐';
  if (/bolo|torta|cheesecake/.test(t)) return '🍰';
  if (/queijo/.test(t)) return '🧀';
  if (/banana/.test(t)) return '🍌';
  if (/ma[çc][ãa]/.test(t)) return '🍎';
  if (/laranja/.test(t)) return '🍊';
  if (/uva/.test(t)) return '🍇';
  if (/morango/.test(t)) return '🍓';
  if (/melancia/.test(t)) return '🍉';
  if (/abacaxi/.test(t)) return '🍍';
  if (/manga/.test(t)) return '🥭';
  if (/tomate/.test(t)) return '🍅';
  if (/cenoura/.test(t)) return '🥕';
  if (/batata/.test(t)) return '🥔';
  if (/cebola/.test(t)) return '🧅';
  if (/alface|couve|espinafre|r[úu]cula|verdura/.test(t)) return '🥬';
  if (/br[óo]colis/.test(t)) return '🥦';
  if (/leite/.test(t)) return '🥛';
  if (/caf[ée]/.test(t)) return '☕';
  if (/cerveja/.test(t)) return '🍺';
  if (/vinho/.test(t)) return '🍷';
  if (/refrigerante|coca|guaran[áa]/.test(t)) return '🥤';
  if (/[áa]gua/.test(t)) return '💧';
  if (/arroz/.test(t)) return '🍚';
  if (/feij[ãa]o/.test(t)) return '🫘';
  if (/macarr[ãa]o|massa/.test(t)) return '🍝';
  if (/[óo]leo/.test(t)) return '🫒';
  if (/sab[ãa]o|detergente|amaciante/.test(t)) return '🧼';
  if (/shampoo|condicionador/.test(t)) return '🧴';
  if (/dipirona|paracetamol|rem[ée]dio|medicamento/.test(t)) return '💊';
  return '🛒';
}

interface SlideProps {
  type: string;
  content: any;
  name?: string | null;
  tenantBranding?: { logo_url?: string; primary_color?: string };
  realProducts?: RealProduct[];
  durationMs?: number;
}

export function SlideRenderer({ type, content, name, tenantBranding, realProducts, durationMs }: SlideProps) {
  switch (type) {
    case 'image':         return <ImageSlide content={content} />;
    case 'video':         return <VideoSlide content={content} />;
    case 'price_table':   return <PriceTableSlide content={content} name={name} branding={tenantBranding} realProducts={realProducts} durationMs={durationMs} />;
    case 'promo_banner':  return <PromoBannerSlide content={content} branding={tenantBranding} />;
    case 'product_carousel': return <PriceTableSlide content={content} name={name} branding={tenantBranding} realProducts={realProducts} durationMs={durationMs} />;
    case 'datetime':      return <DateTimeSlide content={content} branding={tenantBranding} />;
    case 'weather':       return <WeatherSlide content={content} />;
    case 'football':      return <FootballSlide content={content} />;
    case 'html':          return <HtmlSlide content={content} />;
    default:              return <Unsupported type={type} />;
  }
}

function ImageSlide({ content }: any) {
  if (!content.url) {
    return (
      <div className="w-full h-full grid place-items-center bg-black text-white/60">Sem imagem</div>
    );
  }
  return (
    <div className="w-full h-full bg-black relative overflow-hidden">
      <img
        src={content.url}
        alt=""
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: content.fit === 'cover' ? 'cover' : 'contain',
          display: 'block',
        }}
      />
      {!!content.news_ticker && <NewsTickerBar />}
    </div>
  );
}

// Rodapé de notícias + relógio por cima do slide de imagem. Busca as manchetes
// no proxy do backend (que cacheia 10 min); sem dados, a faixa nem aparece —
// a imagem segue limpa. Fontes escalam pela ALTURA da faixa (useFbBox no
// próprio elemento: min(w,h) = altura), então o preview fica proporcional.
function NewsTickerBar() {
  const [items, setItems] = useState<string[]>([]);
  const [now, setNow] = useState(() => new Date());
  const { ref, box } = useFbBox();

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await api.get('/api/signage/news');
        const titles = (r.data?.items ?? []).map((i: any) => i.title).filter(Boolean);
        if (active && titles.length) setItems(titles);
      } catch { /* mantém as manchetes anteriores */ }
    };
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => { active = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (items.length === 0) return null;

  const font = Math.max(10, Math.round(box.base * 0.42));
  const text = items.join('   •   ');
  return (
    <div ref={ref} className="absolute inset-x-0 bottom-0 flex items-stretch text-white" style={{ height: '9%', minHeight: 26, background: 'rgba(0,0,0,0.78)' }}>
      <div className="flex-1 overflow-hidden flex items-center">
        <div className="news-ticker-track" style={{ '--news-dur': `${items.length * 7}s`, fontSize: font, fontWeight: 600 } as React.CSSProperties}>
          <span style={{ paddingInline: '1.5em' }}>{text}</span>
          <span style={{ paddingInline: '1.5em' }}>{text}</span>
        </div>
      </div>
      <div className="flex items-center font-black tabular-nums shrink-0" style={{ fontSize: Math.round(font * 1.1), paddingInline: '0.8em', background: 'rgba(255,255,255,0.14)' }}>
        {now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
}

function VideoSlide({ content }: any) {
  return content.url ? (
    <video src={content.url} className="w-full h-full object-cover" autoPlay muted loop={!!content.loop} />
  ) : (
    <div className="w-full h-full grid place-items-center bg-black text-white/60">[Vídeo sem URL]</div>
  );
}

function PriceTableSlide({ content, name, branding, realProducts, durationMs }: any) {
  // product_carousel: items_visible = produtos por tela (máx 9); o carrossel
  // pagina os `limit` produtos (total da rotação) em telas e rotaciona a cada
  // transition_speed_ms. price_table puro usa limit como nº de itens do grid.
  const isCarousel = !!content.items_visible;
  const itemsVisible = isCarousel ? Math.min(content.items_visible, 9) : 0;
  const fetchLimit = isCarousel ? (content.limit ?? 30) : (content.limit ?? 9);

  // Produtos vêm do servidor JÁ filtrados por categoria/departamento — espelha o endpoint
  // da TV (TvsController.GetProducts). Antes o preview filtrava em JS sobre uma amostra de
  // 200 produtos (ordem alfabética, compartilhada entre slides), então categorias "no fim
  // do alfabeto" apareciam com MENOS itens que na TV (ou caíam no mock).
  const filter = content.filter ?? {};
  const categorias: string[] = Array.isArray(filter.categorias)
    ? filter.categorias.filter((c: any) => typeof c === 'string' && c.trim())
    : (filter.categoria ? [String(filter.categoria)] : []);
  const departamentos: string[] = Array.isArray(filter.departamentos)
    ? filter.departamentos.filter((d: any) => typeof d === 'string' && d.trim())
    : (filter.departamento ? [String(filter.departamento)] : []);
  const onlyPromo = !!filter.only_promo;
  const hasFilter = categorias.length > 0 || departamentos.length > 0 || onlyPromo;
  const fetchKey = JSON.stringify({ categorias, departamentos, onlyPromo });

  // null = ainda carregando → usa o prop compartilhado como ponte até a resposta chegar.
  const [serverProducts, setServerProducts] = useState<{ items: RealProduct[]; usedFallback: boolean } | null>(null);
  useEffect(() => {
    let active = true;
    const params: any = { limit: 200 };
    if (categorias.length) params.categoria = categorias;
    if (departamentos.length) params.departamento = departamentos;
    if (onlyPromo) params.promoOnly = true;
    apiClient.getProducts(params)
      .then(r => {
        const items: RealProduct[] = r.data?.items ?? [];
        if (items.length > 0 || !hasFilter) {
          if (active) setServerProducts({ items, usedFallback: false });
          return;
        }
        // Fallback igual à TV: filtro estrito vazio → devolve os visíveis sem filtro.
        return apiClient.getProducts({ limit: 200 }).then(r2 => {
          if (active) setServerProducts({ items: r2.data?.items ?? [], usedFallback: true });
        });
      })
      .catch(() => { if (active) setServerProducts(null); });
    return () => { active = false; };
  }, [fetchKey]);

  // Com resposta do servidor: já filtrado (preFiltered) — só ordena/intercala/corta.
  // No fallback, replica a TV (todos os visíveis, sem refiltrar por categoria).
  // Sem resposta ainda: usa o prop compartilhado filtrando em JS como antes.
  const allProducts = serverProducts
    ? (serverProducts.usedFallback
        ? pickProducts({}, fetchLimit, false, serverProducts.items, true)
        : pickProducts(content.filter ?? {}, fetchLimit, onlyPromo, serverProducts.items, true))
    : pickProducts(content.filter ?? {}, fetchLimit, onlyPromo, realProducts);

  // Paginação/rotação do carrossel (espelha o tv-app pra preview ser fiel).
  const numPages = isCarousel ? Math.max(1, Math.ceil(allProducts.length / itemsVisible)) : 1;
  const [carouselPage, setCarouselPage] = useState(0);
  useEffect(() => {
    if (!isCarousel || numPages <= 1) return;
    const ms = content.transition_speed_ms || 5000;
    const t = setInterval(() => setCarouselPage(p => (p + 1) % numPages), ms);
    return () => clearInterval(t);
  }, [isCarousel, numPages, content.transition_speed_ms]);
  useEffect(() => { setCarouselPage(0); }, [allProducts.length, itemsVisible]);

  const products = isCarousel
    ? allProducts.slice((carouselPage % numPages) * itemsVisible, (carouselPage % numPages) * itemsVisible + itemsVisible)
    : allProducts;

  const usingMock = !realProducts || realProducts.length === 0;
  const bg = content.background_color ?? '#FFFFFF';
  const titleColor = content.title_color ?? branding?.primary_color ?? '#212121';
  const cardStyle = content.card_style ?? 'default';

  // 1ª busca ainda em andamento e sem nada pra mostrar → carregando, em vez do
  // estado vazio (evita o flash "Nenhum produto encontrado"). Igual ao tv-app.
  if (serverProducts === null && products.length === 0) {
    return (
      <div className="w-full h-full grid place-items-center" style={{ backgroundColor: bg }}>
        <div className="animate-spin rounded-full" style={{ width: 48, height: 48, border: '4px solid rgba(0,0,0,0.15)', borderTopColor: titleColor }} />
      </div>
    );
  }

  if (content.layout === 'featured') {
    return (
      <FeaturedSplitPreview
        products={products}
        content={content}
        branding={branding}
        bg={bg}
        titleColor={titleColor}
        usingMock={usingMock}
        durationMs={durationMs}
      />
    );
  }

  if (content.layout === 'letterboard') {
    return (
      <LetterboardLayout
        products={products}
        content={content}
        name={name}
        usingMock={usingMock}
      />
    );
  }

  if (content.layout === 'menuboard') {
    return (
      <MenuBoardLayout
        products={products}
        content={content}
        name={name}
        usingMock={usingMock}
      />
    );
  }

  if (content.layout === 'list') {
    return (
      <ListLayout
        products={products}
        content={content}
        branding={branding}
        bg={bg}
        titleColor={titleColor}
        usingMock={usingMock}
      />
    );
  }

  const cols = content.layout === 'grid_4' ? 4 : 3;
  const rows = Math.max(1, Math.ceil((products.length || 1) / cols));
  const cells = rows * cols;
  const tier: 'xs' | 'sm' | 'md' | 'lg' | 'xl' =
    cells <= 2 ? 'xl' :
    cells <= 4 ? 'lg' :
    cells <= 6 ? 'md' :
    cells <= 9 ? 'sm' :
                 'xs';

  return (
    <div className="w-full h-full flex flex-col relative" style={{ backgroundColor: bg }}>
      {usingMock && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-amber-500/95 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
          Produtos de exemplo • Cadastre seus produtos para ver os reais
        </div>
      )}

      {/* Header com logo + título */}
      <div className="flex items-center justify-between px-12 pt-10 pb-6">
        {content.show_logo && branding?.logo_url ? (
          <img src={branding.logo_url} className="h-16" />
        ) : (
          <div className="w-16" />
        )}
        {content.title && (
          <h1
            className="text-5xl font-extrabold tracking-tight uppercase text-center flex-1"
            style={{ color: titleColor }}
          >
            {content.title}
          </h1>
        )}
        <div className="w-16" />
      </div>

      {/* Grid de produtos — auto-rows-fr distribui altura igualmente, min-h-0 deixa cards encolherem */}
      <div
        className="flex-1 min-h-0 grid gap-6 px-12 pb-12 auto-rows-fr"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {products.length === 0 ? (
          <div className="col-span-full text-center text-muted-foreground/60 grid place-items-center">
            <div>
              <div className="text-6xl mb-2">📦</div>
              <div>Nenhum produto encontrado para este filtro</div>
            </div>
          </div>
        ) : products.map((p) => (
          <ProductCard
            key={p.codigo}
            p={p}
            showImage={content.show_image !== false}
            cardStyle={cardStyle}
            titleColor={titleColor}
            tier={tier}
          />
        ))}
      </div>
    </div>
  );
}

const TIER_STYLES: Record<'xs' | 'sm' | 'md' | 'lg' | 'xl', { name: string; price: string; old: string; emoji: string; img: string; pad: string }> = {
  xs: { name: 'text-sm',   price: 'text-lg',  old: 'text-[10px]', emoji: 'text-4xl', img: 'max-h-[38%]', pad: 'p-2' },
  sm: { name: 'text-base', price: 'text-2xl', old: 'text-xs',     emoji: 'text-5xl', img: 'max-h-[40%]', pad: 'p-3' },
  md: { name: 'text-2xl',  price: 'text-5xl', old: 'text-sm',     emoji: 'text-7xl', img: 'max-h-[44%]', pad: 'p-4' },
  lg: { name: 'text-3xl',  price: 'text-6xl', old: 'text-base',   emoji: 'text-8xl', img: 'max-h-[48%]', pad: 'p-6' },
  xl: { name: 'text-4xl',  price: 'text-7xl', old: 'text-lg',     emoji: 'text-9xl', img: 'max-h-[52%]', pad: 'p-8' },
};

function ProductCard({ p, showImage, cardStyle, titleColor, tier = 'sm' }: any) {
  const hasPromo = p.preco_promo_cents;
  const discount = hasPromo ? Math.round((1 - p.preco_promo_cents / p.preco_cents) * 100) : 0;
  const cardClass =
    cardStyle === 'premium'   ? 'border-2 shadow-2xl rounded-2xl bg-white'
  : cardStyle === 'rustic'    ? 'border-2 border-amber-200 rounded-2xl bg-amber-50/60'
  : cardStyle === 'minimal'   ? 'border-0 rounded-md bg-white shadow'
  : cardStyle === 'elegant'   ? 'border border-amber-700 rounded-none bg-black'
  : cardStyle === 'neon'      ? 'border-2 border-cyan-400 rounded-2xl bg-slate-900'
  : cardStyle === 'playful'   ? 'border-4 border-pink-400 rounded-3xl bg-white'
  : 'border rounded-lg bg-white';

  const t = TIER_STYLES[tier as keyof typeof TIER_STYLES] ?? TIER_STYLES.sm;

  return (
    <div className={`relative ${t.pad} flex flex-col items-center h-full min-h-0 overflow-hidden ${cardClass}`}>
      {hasPromo && (
        <div className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg z-10">
          -{discount}%
        </div>
      )}
      {showImage && (
        p.imagem_url ? (
          <img src={p.imagem_url} className={`${t.img} aspect-square object-contain shrink-0`} alt={p.descricao} />
        ) : (
          <div className={`${t.emoji} leading-none shrink-0`}>{p.emoji ?? '🛒'}</div>
        )
      )}
      <div
        className={`text-center font-bold ${t.name} leading-tight line-clamp-2 px-1 w-full min-h-0 flex-1 grid place-items-center`}
        style={{ color: titleColor }}
      >
        <span className="line-clamp-2">{p.descricao}</span>
      </div>
      <div className="text-center shrink-0 w-full">
        {hasPromo ? (
          <>
            <div className={`${t.old} line-through opacity-50 leading-none`}>{formatCents(p.preco_cents)}</div>
            <div className={`${t.price} font-black text-red-600 leading-none mt-0.5`}>{formatCents(p.preco_promo_cents)}</div>
          </>
        ) : (
          <div className={`${t.price} font-black leading-none`} style={{ color: titleColor }}>{formatCents(p.preco_cents)}</div>
        )}
      </div>
    </div>
  );
}

function ListLayout({ products, content, branding, bg, titleColor, usingMock }: any) {
  const showImage = content.show_image !== false;
  const cols = 2;
  const rows = Math.ceil(products.length / cols);

  const zebraEnabled  = content.list_zebra === true;
  const zebraEven     = content.list_zebra_color_even || '#f1f5f9';
  const zebraOdd      = content.list_zebra_color_odd  || '#ffffff';

  const t =
    rows <= 4  ? { name: 'text-5xl', price: 'text-6xl', old: 'text-2xl', img: 'h-24 w-24', emoji: 'text-7xl', pad: 'py-4 px-6' }
  : rows <= 6  ? { name: 'text-4xl', price: 'text-5xl', old: 'text-xl',  img: 'h-20 w-20', emoji: 'text-6xl', pad: 'py-3 px-5' }
  : rows <= 9  ? { name: 'text-3xl', price: 'text-4xl', old: 'text-lg',  img: 'h-16 w-16', emoji: 'text-5xl', pad: 'py-2 px-4' }
  : rows <= 12 ? { name: 'text-2xl', price: 'text-3xl', old: 'text-base',img: 'h-14 w-14', emoji: 'text-4xl', pad: 'py-2 px-3' }
  :              { name: 'text-xl',  price: 'text-2xl', old: 'text-sm',  img: 'h-12 w-12', emoji: 'text-3xl', pad: 'py-1.5 px-3' };

  return (
    <div className="w-full h-full flex flex-col relative" style={{ backgroundColor: bg }}>
      {usingMock && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-amber-500/95 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
          Produtos de exemplo • Cadastre seus produtos para ver os reais
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-12 pt-10 pb-6">
        {content.show_logo && branding?.logo_url ? (
          <img src={branding.logo_url} className="h-16" />
        ) : (
          <div className="w-16" />
        )}
        {content.title && (
          <h1 className="text-5xl font-extrabold tracking-tight uppercase text-center flex-1" style={{ color: titleColor }}>
            {content.title}
          </h1>
        )}
        <div className="w-16" />
      </div>

      {/* Grid 2 colunas com zebra opcional */}
      <div
        className="flex-1 min-h-0 grid px-12 pb-10 auto-rows-fr"
        style={{
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: zebraEnabled ? '0 3rem' : '0.5rem 3rem',
        }}
      >
        {products.length === 0 ? (
          <div className="col-span-full text-center text-muted-foreground/60 grid place-items-center">
            <div><div className="text-6xl mb-2">📦</div><div>Nenhum produto encontrado</div></div>
          </div>
        ) : products.map((p: any, i: number) => {
          const rowIndex = Math.floor(i / cols);
          const cardBg = zebraEnabled
            ? (rowIndex % 2 === 0 ? zebraEven : zebraOdd)
            : 'white';
          const hasPromo = !!p.preco_promo_cents;
          const discount = hasPromo ? Math.round((1 - p.preco_promo_cents / p.preco_cents) * 100) : 0;
          const borderRadius = zebraEnabled ? '0' : '0.5rem';
          const boxShadow = zebraEnabled ? 'none' : '0 1px 3px rgba(0,0,0,0.06)';
          const border = zebraEnabled ? 'none' : '1px solid rgba(0,0,0,0.05)';
          return (
            <div
              key={p.codigo}
              className={`flex items-center gap-4 ${t.pad} min-w-0`}
              style={{ backgroundColor: cardBg, borderRadius, boxShadow, border }}
            >
              {showImage && (
                p.imagem_url
                  ? <img src={p.imagem_url} className={`${t.img} object-contain shrink-0`} alt={p.descricao} />
                  : <div className={`${t.emoji} leading-none shrink-0`}>{p.emoji ?? '🛒'}</div>
              )}
              <div
                className={`flex-1 min-w-0 font-bold uppercase tracking-tight ${t.name} truncate`}
                style={{ color: titleColor }}
                title={p.descricao}
              >
                {p.descricao}
              </div>
              <div className="text-right shrink-0 flex flex-col items-end leading-none">
                {hasPromo ? (
                  <>
                    <div className={`${t.old} line-through opacity-50`}>{formatCents(p.preco_cents)}</div>
                    <div className="flex items-baseline gap-2">
                      <span className={`${t.price} font-black text-red-600 tabular-nums`}>{formatCents(p.preco_promo_cents)}</span>
                      <span className="text-xs font-bold bg-red-600 text-white px-2 py-0.5 rounded-full">-{discount}%</span>
                    </div>
                  </>
                ) : (
                  <div className={`${t.price} font-black tabular-nums`} style={{ color: titleColor }}>
                    {formatCents(p.preco_cents)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FeaturedSplitPreview({ products, content, branding, bg, titleColor, usingMock, durationMs }: any) {
  const pageSize = Math.max(1, Math.min(20, Number(content.list_page_size ?? 10)));
  const totalProducts = products?.length ?? 0;
  const numPages = Math.max(1, Math.ceil(totalProducts / pageSize));
  const [pageIdx, setPageIdx] = useState(0);
  const [featuredIdx, setFeaturedIdx] = useState(0);

  // Tempo da página da lista: divide a duração total do slide entre as páginas;
  // se não vier durationMs, cai no rotation manual ou 8s por página.
  const pageMs = numPages > 1
    ? (durationMs && durationMs > 0
        ? Math.max(1500, Math.floor(durationMs / numPages))
        : (content.list_page_ms ?? 8000))
    : 0;

  // Avança a página da lista
  useEffect(() => {
    if (numPages <= 1) return;
    const t = setInterval(() => setPageIdx(i => (i + 1) % numPages), pageMs);
    return () => clearInterval(t);
  }, [numPages, pageMs]);

  // Reseta página/destaque ao mudar lista
  useEffect(() => { setPageIdx(0); setFeaturedIdx(0); }, [totalProducts]);
  // Ao mudar de página, reinicia o destaque
  useEffect(() => { setFeaturedIdx(0); }, [pageIdx]);

  // Produtos visíveis na página atual
  const pageStart = pageIdx * pageSize;
  const pageProducts = (products ?? []).slice(pageStart, pageStart + pageSize);

  // Destaque da direita rotaciona apenas dentro da página atual
  const rotationMs = content.featured_rotation_ms ?? 5000;
  useEffect(() => {
    if (!pageProducts || pageProducts.length <= 1) return;
    const t = setInterval(() => setFeaturedIdx(i => (i + 1) % pageProducts.length), rotationMs);
    return () => clearInterval(t);
  }, [pageProducts.length, rotationMs, pageIdx]);

  if (!products || products.length === 0) {
    return (
      <div className="w-full h-full grid place-items-center text-muted-foreground/60" style={{ backgroundColor: bg }}>
        Nenhum produto encontrado
      </div>
    );
  }

  const featured = pageProducts[Math.min(featuredIdx, Math.max(0, pageProducts.length - 1))] ?? products[0];
  // Trata "" e whitespace como ausência de override — assim quando o usuário limpa o campo no editor,
  // o renderer volta a herdar do branding/tema em vez de aplicar string vazia.
  const pickStr = (v: any, fallback: string) =>
    typeof v === 'string' && v.trim() ? v : fallback;
  const headerBg = pickStr(content.header_color, branding?.primary_color ?? '#7C2D12');
  const headerText = pickStr(content.header_text_color, '#FFFFFF');
  const accent = pickStr(content.accent_color, '#D7A86E');
  const leftTitle = content.left_title ?? content.title ?? 'Ofertas';
  const rightTitle = content.right_title ?? 'Destaque';
  const subtitle = content.subtitle;
  const logoUrl = pickStr(content.logo_url, branding?.logo_url ?? '');
  const hasPromo = !!featured.preco_promo_cents;

  return (
    <div className="w-full h-full flex flex-col relative" style={{ backgroundColor: bg }}>
      {usingMock && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-amber-500/95 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
          Produtos de exemplo • Cadastre seus produtos para ver os reais
        </div>
      )}

      {/* Header */}
      <div
        className="flex items-center"
        style={{
          height: 'clamp(80px, 18%, 200px)',
          backgroundColor: headerBg,
          borderBottom: `4px solid ${accent}`,
        }}
      >
        <div className="flex-1 grid place-items-center">
          <div className="font-black uppercase tracking-wide text-center" style={{ color: headerText, fontSize: 'clamp(1.5rem, 5vw, 5rem)' }}>
            {leftTitle}
          </div>
        </div>
        <div
          className="rounded-full bg-white grid place-items-center overflow-hidden mx-3"
          style={{ width: 'clamp(60px, 12%, 140px)', aspectRatio: '1', border: `3px solid ${accent}` }}
        >
          {logoUrl ? (
            <img src={logoUrl} className="w-full h-full object-contain" />
          ) : (
            <div className="text-5xl">🏷️</div>
          )}
        </div>
        <div className="flex-1 grid place-items-center">
          <div className="font-black uppercase tracking-wide text-center" style={{ color: headerText, fontSize: 'clamp(1.5rem, 5vw, 5rem)' }}>
            {rightTitle}
          </div>
        </div>
      </div>

      {subtitle && (
        <div className="text-center py-1.5 italic text-sm md:text-base" style={{ backgroundColor: accent, color: titleColor }}>
          {subtitle}
        </div>
      )}

      {/* Body split */}
      <div className="flex-1 min-h-0 flex gap-6 px-6 py-4">
        {/* Lista esquerda (paginada) */}
        {/* min-w-0: sem isso, descrição longa estoura a coluna (min-width:auto do
            flex item), quebra o truncate e empurra o card de destaque p/ fora da tela. */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col justify-between relative">
          {numPages > 1 && (
            <div className="absolute top-0 right-0 -mt-1 text-xs font-semibold opacity-60" style={{ color: titleColor }}>
              {pageIdx + 1}/{numPages}
            </div>
          )}
          {pageProducts.map((p: any, i: number) => {
            const isActive = i === featuredIdx;
            const price = p.preco_promo_cents ?? p.preco_cents;
            return (
              <div
                key={p.codigo}
                className="flex items-center gap-4 px-2 py-1 rounded-md transition-colors"
                style={{ backgroundColor: isActive ? `${accent}40` : 'transparent' }}
              >
                <div
                  className="flex-1 min-w-0 truncate font-semibold uppercase"
                  style={{ color: titleColor, fontSize: 'clamp(1.4rem, 3.2vw, 3.4rem)', fontWeight: isActive ? 800 : 600 }}
                >
                  {p.descricao}
                </div>
                <div
                  className="font-black tabular-nums"
                  style={{ color: titleColor, fontSize: 'clamp(1.55rem, 3.5vw, 3.7rem)' }}
                >
                  {formatCents(price).replace('R$', '').trim()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Destaque direita */}
        <div
          className="flex-1 min-w-0 min-h-0 flex flex-col items-center justify-between p-5 rounded-2xl bg-white shadow-2xl"
          style={{ border: `2px solid ${accent}` }}
        >
          <div
            className="text-center font-black uppercase mb-2 line-clamp-2"
            style={{ color: titleColor, fontSize: 'clamp(2.3rem, 5.2vw, 5.8rem)' }}
          >
            {featured.descricao}
          </div>
          <div className="flex-1 w-full grid place-items-center min-h-0">
            {featured.imagem_url ? (
              <img src={featured.imagem_url} className="max-h-full max-w-[85%] object-contain" />
            ) : (
              <div style={{ fontSize: 'clamp(4rem, 14vw, 14rem)' }}>{featured.emoji ?? '🛒'}</div>
            )}
          </div>
          <div className="text-center mt-2">
            {hasPromo && (
              <div className="line-through opacity-50" style={{ color: titleColor, fontSize: 'clamp(1rem, 1.8vw, 1.75rem)' }}>
                {formatCents(featured.preco_cents)}
              </div>
            )}
            <div
              className="font-black leading-none"
              style={{
                color: hasPromo ? '#DC2626' : titleColor,
                fontSize: 'clamp(2.5rem, 8vw, 8rem)',
              }}
            >
              {formatCents(featured.preco_promo_cents ?? featured.preco_cents)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Menu Board — letreiro de lanchonete estilo placa de feltro.
 * Fundo preto com sulcos horizontais (lembrando placa de letras encaixáveis),
 * texto amarelo em maiúsculas e linhas vermelhas dividindo seções.
 * Agrupa por categoria (até 2 colunas); se só há 1 categoria, divide os itens em 2 colunas.
 */
function MenuBoardLayout({ products, content, name, usingMock }: any) {
  const bg = '#141414';
  const accent = '#FACC15';     // amarelo principal (preços e itens)
  const heading = '#FBBF24';    // amarelo do título de seção
  const divider = '#DC2626';    // linha vermelha
  const promoColor = '#F87171'; // vermelho claro p/ preços em promoção
  const title = (name ?? content.title ?? '').toString().trim();

  // Agrupa por categoria; se só uma categoria com muitos itens, divide ao meio em 2 colunas.
  const groups: { title: string; items: any[] }[] = (() => {
    const byCat = new Map<string, any[]>();
    for (const p of products ?? []) {
      const key = ((p?.categoria ?? '') as string).toString().trim().toUpperCase();
      const k = key || 'CARDÁPIO';
      if (!byCat.has(k)) byCat.set(k, []);
      byCat.get(k)!.push(p);
    }
    const entries = Array.from(byCat.entries());
    if (entries.length === 1) {
      const [cat, items] = entries[0];
      if (items.length >= 6) {
        const half = Math.ceil(items.length / 2);
        return [
          { title: cat, items: items.slice(0, half) },
          { title: '', items: items.slice(half) },
        ];
      }
      return [{ title: cat, items }];
    }
    entries.sort((a, b) => b[1].length - a[1].length);
    return entries.slice(0, 2).map(([t, items]) => ({ title: t, items }));
  })();

  const cols = Math.max(1, groups.length);
  const maxRows = Math.max(...groups.map(g => g.items.length), 1);
  const t =
    maxRows <= 5  ? { name: 'text-4xl', price: 'text-4xl', head: 'text-5xl' }
  : maxRows <= 8  ? { name: 'text-3xl', price: 'text-3xl', head: 'text-4xl' }
  : maxRows <= 12 ? { name: 'text-2xl', price: 'text-2xl', head: 'text-3xl' }
  :                 { name: 'text-xl',  price: 'text-xl',  head: 'text-2xl' };

  return (
    <div
      className="w-full h-full flex flex-col relative overflow-hidden"
      style={{
        backgroundColor: bg,
        // Sulcos horizontais finos lembrando uma placa de letras de feltro
        backgroundImage:
          'repeating-linear-gradient(0deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 26px), linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)',
        boxShadow: 'inset 0 0 140px rgba(0,0,0,0.7)',
        fontFamily: '"Courier New", "Lucida Console", monospace',
      }}
    >
      {usingMock && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-amber-500/95 text-black text-xs font-bold px-3 py-1 rounded-full shadow">
          Produtos de exemplo • Cadastre seus produtos para ver os reais
        </div>
      )}

      {/* Título principal com linha vermelha */}
      {title && (
        <div className="text-center pt-8 pb-4 px-6 shrink-0">
          <div
            className="font-extrabold tracking-[0.15em] uppercase"
            style={{
              color: heading,
              fontSize: 'clamp(2rem, 5.5vw, 4.5rem)',
              textShadow: '2px 2px 0 rgba(0,0,0,0.6)',
            }}
          >
            {title}
          </div>
          <div className="flex justify-center mt-2">
            <div style={{ height: '3px', width: 'min(70%, 700px)', backgroundColor: divider }} />
          </div>
        </div>
      )}

      {/* Colunas com itens */}
      <div
        className="flex-1 min-h-0 grid gap-x-16 px-12 pb-10 pt-2"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {groups.map((g, idx) => (
          <div key={`${g.title}-${idx}`} className="flex flex-col min-h-0">
            {/* Header da seção (categoria) */}
            <div className="text-center mb-4 shrink-0" style={{ minHeight: g.title ? undefined : '1.5em' }}>
              {g.title && (
                <>
                  <div
                    className={`font-extrabold tracking-[0.12em] uppercase ${t.head}`}
                    style={{ color: heading, textShadow: '2px 2px 0 rgba(0,0,0,0.6)' }}
                  >
                    {g.title}
                  </div>
                  <div className="flex justify-center mt-1">
                    <div style={{ height: '2px', width: '85%', backgroundColor: divider }} />
                  </div>
                </>
              )}
            </div>

            {/* Itens — faixas de altura igual, zebradas (content.zebra !== false). */}
            <div className="flex-1 min-h-0 flex flex-col">
              {g.items.map((p, i) => {
                const price = p.preco_promo_cents ?? p.preco_cents;
                const isPromo = !!p.preco_promo_cents;
                const zebraOn = content.zebra !== false;
                const stripe = zebraOn && i % 2 === 1 ? 'rgba(255,255,255,0.055)' : 'transparent';
                return (
                  <div
                    key={p.codigo}
                    className={`flex-1 flex items-center justify-between gap-3 font-bold uppercase ${t.name}`}
                    style={{
                      color: accent,
                      letterSpacing: '0.05em',
                      textShadow: '1px 1px 0 rgba(0,0,0,0.7)',
                      paddingInline: 'clamp(0.6rem, 1.6vw, 1.6rem)',
                      backgroundColor: stripe,
                      borderRadius: 'clamp(0.4rem, 1vw, 1rem)',
                    }}
                  >
                    <span className="truncate flex-1">{p.descricao}</span>
                    <span
                      className={`tabular-nums whitespace-nowrap ${t.price}`}
                      style={{ color: isPromo ? promoColor : accent }}
                    >
                      {formatCents(price)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Letterboard / Split-flap — placar estilo aeroporto antigo.
 * 2 colunas, fundo escuro, texto âmbar, monospace.
 * Cada caractere passa por um efeito de "embaralhar" antes de assentar.
 */
function LetterboardLayout({ products, content, name, usingMock }: any) {
  // Layout estilo aeroporto: cores fixas (preto + amarelo) — ignora cores do template.
  const bg = '#0A0A0A';
  const accent = '#FACC15';      // amarelo âmbar p/ nomes
  const priceColor = '#FFFFFF';  // branco p/ preços
  // Título vem do nome da slide (campo "Nome" do editor); content.title é fallback
  // pra slides antigos que ainda guardam o título no JSON.
  const title = (name ?? content.title ?? '').toString().trim();

  const half = Math.ceil(products.length / 2);
  const left = products.slice(0, half);
  const right = products.slice(half);

  const rowsPerCol = Math.max(left.length, right.length, 1);
  const rowFontTier =
    rowsPerCol <= 4  ? { name: 'text-5xl md:text-7xl', price: 'text-5xl md:text-7xl' }
  : rowsPerCol <= 7  ? { name: 'text-4xl md:text-5xl', price: 'text-4xl md:text-5xl' }
  : rowsPerCol <= 10 ? { name: 'text-2xl md:text-4xl', price: 'text-2xl md:text-4xl' }
  : rowsPerCol <= 14 ? { name: 'text-xl md:text-3xl',  price: 'text-xl md:text-3xl'  }
                     : { name: 'text-lg md:text-2xl',  price: 'text-lg md:text-2xl'  };

  return (
    <div
      className="w-full h-full flex flex-col font-mono relative overflow-hidden"
      style={{
        backgroundColor: bg,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
        backgroundSize: '6px 6px',
      }}
    >
      {usingMock && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 bg-amber-500/95 text-black text-xs font-bold px-3 py-1 rounded-full shadow">
          Produtos de exemplo • Cadastre seus produtos para ver os reais
        </div>
      )}

      {/* Título no topo: ***  TITULO  *** */}
      {title && (
        <div className="text-center pt-6 pb-4 px-6">
          <FlapText
            text={`***   ${title.toUpperCase()}   ***`}
            color={accent}
            className="font-black tracking-widest text-3xl md:text-5xl leading-none"
          />
        </div>
      )}

      {/* Duas colunas com lista de preços */}
      <div className="flex-1 min-h-0 grid grid-cols-2 gap-x-10 px-8 pb-6">
        <PriceColumn items={left} accent={accent} priceColor={priceColor} font={rowFontTier} indexBase={0} />
        <PriceColumn items={right} accent={accent} priceColor={priceColor} font={rowFontTier} indexBase={left.length} />
      </div>
    </div>
  );
}

function PriceColumn({
  items,
  accent,
  priceColor,
  font,
  indexBase,
}: {
  items: any[];
  accent: string;
  priceColor: string;
  font: { name: string; price: string };
  indexBase: number;
}) {
  if (items.length === 0) return <div />;
  return (
    <div className="flex flex-col justify-around min-h-0">
      {items.map((p, i) => {
        const price = p.preco_promo_cents ?? p.preco_cents;
        const priceText = formatCents(price).replace('R$', '').trim();
        const promo = !!p.preco_promo_cents;
        return (
          <div
            key={p.codigo}
            className={`flex items-baseline justify-between gap-3 font-black uppercase tracking-wider ${font.name}`}
            style={{ borderBottom: '1px dashed rgba(255,255,255,0.08)', paddingBlock: '0.25rem' }}
          >
            <span className="truncate" style={{ color: accent }}>
              <FlapText text={p.descricao} delay={(indexBase + i) * 60} color={accent} />
            </span>
            <span className={`tabular-nums whitespace-nowrap ${font.price}`} style={{ color: promo ? '#F87171' : priceColor }}>
              <FlapText text={priceText} delay={(indexBase + i) * 60 + 180} color={promo ? '#F87171' : priceColor} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Texto com efeito split-flap: cada caractere embaralha antes de assentar.
 * Usa stagger por caractere (delay incremental) para o efeito de cascata.
 */
const FLAP_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ ,.$/-';

function FlapText({
  text,
  delay = 0,
  color,
  className,
}: {
  text: string;
  delay?: number;
  color?: string;
  className?: string;
}) {
  const chars = (text ?? '').toUpperCase().split('');
  return (
    <span className={className} style={{ color, perspective: 400 }}>
      {chars.map((c, i) => (
        <FlapChar key={`${i}-${c}`} target={c} delay={delay + i * 18} />
      ))}
    </span>
  );
}

function FlapChar({ target, delay }: { target: string; delay: number }) {
  const [c, setC] = useState(target === ' ' ? ' ' : '?');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (target === ' ') {
      setC(' ');
      return;
    }
    let frame = 0;
    const total = 5 + Math.floor(Math.random() * 6);
    let interval: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        frame++;
        setTick((t) => t + 1);
        if (frame >= total) {
          setC(target);
          if (interval) clearInterval(interval);
        } else {
          setC(FLAP_CHARS[Math.floor(Math.random() * FLAP_CHARS.length)]);
        }
      }, 55);
    }, delay);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [target, delay]);

  return (
    <span
      key={tick}
      style={{
        display: 'inline-block',
        animation: 'flap-slide 110ms ease-out',
        transformOrigin: 'center top',
      }}
    >
      {c === ' ' ? ' ' : c}
    </span>
  );
}

export function iconEmoji(icon: string): string {
  switch (icon) {
    case 'fire': return '🔥';
    case 'star': return '⭐';
    case 'gift': return '🎁';
    case 'meat': return '🥩';
    case 'bread': return '🥖';
    case 'coffee': return '☕';
    case 'cart': return '🛒';
    case 'sparkles': return '✨';
    default: return '';
  }
}

function mapLegacyPromoTemplate(t: string): string {
  switch (t) {
    case 'classic': return 'split';
    case 'modern':  return 'spotlight';
    case 'urgency': return 'urgency';
    case 'minimal': return 'minimal';
    case 'bold':    return 'bold';
    case 'vitrine': return 'vitrine';
    case 'spotlight':
    case 'split':   return t;
    default:        return 'spotlight';
  }
}

interface PromoProps {
  headline: string;
  subtitle: string;
  price: string;
  priceOld: string;
  cta: string;
  validity: string;
  icon: string;
  bg: string;
  fg: string;
  accent: string;
  bgImage: string;
  overlay: number;
  countdownTo: string;
  showLogo: boolean;
  logoUrl?: string;
  productImageUrl?: string;
  // Emoji de fallback p/ produto de catálogo sem imagem_url (modo automático).
  // Vazio em banner manual: se o operador não pôs foto, não forçamos emoji.
  productEmoji?: string;
}

function PromoBannerSlide({ content, branding }: any) {
  const isAuto = content?.mode === 'auto';
  const [autoProducts, setAutoProducts] = useState<any[]>([]);
  const [autoIdx, setAutoIdx] = useState(0);

  useEffect(() => {
    if (!isAuto) return;
    const categoria = content.auto_categoria ? { categoria: content.auto_categoria } : {};
    // Mesma lógica do endpoint da TV (TvsController): filtro estrito (só promo) →
    // se vier vazio, refaz SEM filtro e devolve os visíveis. Sem esse fallback o
    // preview ficava vazio (caindo no headline manual "OFERTA") quando o catálogo
    // não tinha nenhum produto com preço promocional — divergindo do que a TV exibe.
    apiClient.getProducts({ promoOnly: true, ...categoria, limit: 50 }).then(r => {
      const items = r.data?.items || [];
      if (items.length > 0) {
        setAutoProducts(items);
        setAutoIdx(0);
        return;
      }
      return apiClient.getProducts({ ...categoria, limit: 50 }).then(r2 => {
        setAutoProducts(r2.data?.items || []);
        setAutoIdx(0);
      });
    }).catch(() => {});
  }, [isAuto, content.auto_categoria]);

  useEffect(() => {
    if (!isAuto || autoProducts.length === 0) return;
    const ms = content.auto_time_per_product_ms || 10000;
    const t = setInterval(() => setAutoIdx(i => (i + 1) % autoProducts.length), ms);
    return () => clearInterval(t);
  }, [isAuto, autoProducts.length, content.auto_time_per_product_ms]);

  const autoCurrent = isAuto && autoProducts.length > 0 ? autoProducts[autoIdx % autoProducts.length] : null;

  const template = mapLegacyPromoTemplate(String(content.template ?? 'spotlight'));
  const props: PromoProps = {
    headline: autoCurrent ? (autoCurrent.descricao ?? '') : (content.headline ?? ''),
    subtitle: autoCurrent ? (autoCurrent.marca ?? autoCurrent.categoria ?? '') : (content.subtitle ?? ''),
    price: autoCurrent
      ? formatCents(autoCurrent.preco_promo_cents ?? autoCurrent.preco_cents)
      : (content.price ?? ''),
    priceOld: autoCurrent && autoCurrent.preco_promo_cents
      ? formatCents(autoCurrent.preco_cents)
      : (content.price_old ?? ''),
    cta: content.cta ?? '',
    validity: content.validity ?? '',
    icon: content.icon ? iconEmoji(content.icon) : '',
    bg: content.background_color ?? '#C81E1E',
    fg: content.text_color ?? '#FFFFFF',
    accent: content.accent_color ?? '#FFD700',
    bgImage: content.background_url ?? '',
    overlay: typeof content.background_overlay === 'number' ? content.background_overlay : 35,
    countdownTo: content.countdown_to ?? '',
    showLogo: !!content.show_logo,
    logoUrl: branding?.logo_url,
    productImageUrl: autoCurrent ? (autoCurrent.imagem_url ?? '') : (content.product_image_url ?? ''),
    productEmoji: autoCurrent && !autoCurrent.imagem_url ? guessEmoji(autoCurrent) : '',
  };

  switch (template) {
    case 'split':     return <PromoSplit {...props} />;
    case 'urgency':   return <PromoUrgency {...props} />;
    case 'minimal':   return <PromoMinimal {...props} />;
    case 'bold':      return <PromoBold {...props} />;
    case 'vitrine':   return <PromoVitrine {...props} />;
    case 'spotlight':
    default:          return <PromoSpotlight {...props} />;
  }
}

function PromoBg({ color, image, overlay }: { color: string; image?: string; overlay: number }) {
  return (
    <>
      <div className="absolute inset-0" style={{ backgroundColor: color }} />
      {image && (
        <>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${image})` }} />
          <div className="absolute inset-0" style={{ backgroundColor: color, opacity: Math.max(0, Math.min(100, overlay)) / 100 }} />
        </>
      )}
    </>
  );
}

function PromoLogo({ url }: { url?: string }) {
  if (!url) return null;
  return <img src={url} className="absolute top-10 left-10 h-20 z-20 object-contain drop-shadow-lg" alt="" />;
}

/** Textura sutil de "grão" — dá premium feel sem competir com o conteúdo. */
const NOISE_BG = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.45 0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")";

function PromoNoise() {
  return (
    <div
      className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-30"
      style={{ backgroundImage: NOISE_BG, backgroundSize: '180px 180px' }}
    />
  );
}

/**
 * Foto do produto em destaque — moldura branca, sombra forte, leve rotação para parecer adesivo/cartaz.
 * Usada em todos os templates de promo quando productImageUrl está presente.
 */
function ProductPhoto({
  url,
  emoji,
  size = 'lg',
  rotate = 0,
}: { url?: string; emoji?: string; size?: 'md' | 'lg' | 'xl'; rotate?: number }) {
  const dims = {
    md: 'w-[24rem] h-[24rem]',
    lg: 'w-[34rem] h-[34rem]',
    xl: 'w-[44rem] h-[44rem]',
  } as const;
  const emojiSize = {
    md: 'text-[12rem]',
    lg: 'text-[18rem]',
    xl: 'text-[24rem]',
  } as const;
  return (
    <div
      className={`${dims[size]} relative rounded-3xl overflow-hidden bg-white grid place-items-center`}
      style={{
        transform: `rotate(${rotate}deg)`,
        boxShadow: '0 40px 80px -10px rgba(0,0,0,0.55), 0 0 0 6px rgba(255,255,255,0.95), 0 0 0 8px rgba(0,0,0,0.18)',
      }}
    >
      {url ? (
        <img src={url} alt="" className="w-full h-full object-contain" style={{ padding: '8%' }} />
      ) : (
        <span className={`${emojiSize[size]} leading-none`} style={{ lineHeight: 1 }}>{emoji}</span>
      )}
    </div>
  );
}

function PriceBurst({
  price,
  priceOld,
  accent,
  fg,
  size = 'md',
  rotate = -8,
}: { price: string; priceOld?: string; accent: string; fg: string; size?: 'sm' | 'md' | 'lg' | 'xl'; rotate?: number }) {
  if (!price) return null;
  const sizes = {
    sm: { box: 'w-56 h-56',          price: 'text-5xl', old: 'text-lg'   },
    md: { box: 'w-80 h-80',          price: 'text-7xl', old: 'text-2xl' },
    lg: { box: 'w-[32rem] h-[32rem]', price: 'text-9xl', old: 'text-4xl' },
    xl: { box: 'w-[42rem] h-[42rem]', price: 'text-[10rem]', old: 'text-5xl' },
  } as const;
  const s = sizes[size];
  return (
    <div
      className={`${s.box} relative grid place-items-center rounded-full`}
      style={{
        backgroundColor: accent,
        color: fg,
        transform: `rotate(${rotate}deg)`,
        boxShadow: '0 35px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.1) inset',
      }}
    >
      {/* anel duplo serrilhado */}
      <div className="absolute inset-3 rounded-full border-2 border-dashed opacity-40" style={{ borderColor: fg }} />
      <div className="absolute inset-7 rounded-full border opacity-25" style={{ borderColor: fg }} />
      <div className="text-center px-8">
        {priceOld && (
          <div className={`${s.old} line-through opacity-60 leading-none mb-2 font-bold tracking-tight`}>
            {priceOld}
          </div>
        )}
        <div className={`${s.price} font-black leading-[0.85] tracking-tight whitespace-nowrap`} style={{ letterSpacing: '-0.04em' }}>
          {price}
        </div>
      </div>
    </div>
  );
}

function PromoSpotlight(p: PromoProps) {
  // Composição: spot radial atrás + selo lateral à direita + headline ocupando o centro
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ color: p.fg }}>
      <PromoBg color={p.bg} image={p.bgImage} overlay={p.overlay} />
      {/* Halo radial — efeito spotlight */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '50%',
          left: '35%',
          transform: 'translate(-50%, -50%)',
          width: '90vh',
          height: '90vh',
          background: `radial-gradient(circle, ${p.accent}55 0%, ${p.accent}00 60%)`,
          filter: 'blur(20px)',
        }}
      />
      {/* Beam diagonal */}
      <div
        className="absolute pointer-events-none opacity-25"
        style={{
          top: '-20%',
          left: '20%',
          width: '40%',
          height: '140%',
          background: `linear-gradient(180deg, ${p.fg}30, transparent)`,
          transform: 'rotate(15deg)',
          filter: 'blur(40px)',
        }}
      />
      <PromoNoise />
      {p.showLogo && <PromoLogo url={p.logoUrl} />}

      {/* Validity ribbon vindo do topo */}
      {p.validity && (
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 px-8 py-3 text-base font-black uppercase tracking-[0.3em] shadow-2xl"
          style={{
            backgroundColor: p.accent,
            color: p.bg,
            clipPath: 'polygon(0 0, 100% 0, calc(100% - 16px) 100%, 16px 100%)',
          }}
        >
          {p.validity}
        </div>
      )}

      <div className="relative z-10 w-full h-full grid grid-cols-12 items-center px-16">
        {/* Coluna esquerda: ícone + headline + subtitle + cta */}
        <div className="col-span-7 flex flex-col gap-6 pl-4">
          {p.icon && (
            <div className="text-[10rem] leading-none drop-shadow-2xl -mb-4 self-start" style={{ filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.4))' }}>
              {p.icon}
            </div>
          )}
          {p.headline && (
            <h1
              className="font-black uppercase leading-[0.82] break-words drop-shadow-2xl"
              style={{
                fontSize: 'clamp(5rem, 13vw, 14rem)',
                letterSpacing: '-0.045em',
                textShadow: '0 12px 40px rgba(0,0,0,0.45)',
              }}
            >
              {p.headline}
            </h1>
          )}
          {p.subtitle && (
            <p
              className="font-bold leading-tight max-w-[90%]"
              style={{ color: p.accent, fontSize: 'clamp(1.5rem, 2.6vw, 2.75rem)', letterSpacing: '-0.01em' }}
            >
              {p.subtitle}
            </p>
          )}
          {p.cta && (
            <div className="flex items-center gap-4 mt-2">
              <div
                className="px-10 py-5 rounded-full text-2xl font-black uppercase tracking-wider shadow-2xl"
                style={{ backgroundColor: p.fg, color: p.bg, letterSpacing: '0.08em' }}
              >
                {p.cta}
              </div>
              <div className="text-3xl">→</div>
            </div>
          )}
        </div>

        {/* Coluna direita: foto do produto (se houver) com selo de preço sobreposto; senão price burst sozinho */}
        <div className="col-span-5 flex justify-end items-center pr-4 relative">
          {(p.productImageUrl || p.productEmoji) ? (
            <div className="relative">
              <ProductPhoto url={p.productImageUrl} emoji={p.productEmoji} size="lg" rotate={-3} />
              {p.price && (
                <div className="absolute -bottom-10 -left-16">
                  <PriceBurst price={p.price} priceOld={p.priceOld} accent={p.accent} fg={p.bg} size="md" rotate={-12} />
                </div>
              )}
            </div>
          ) : (
            p.price && <PriceBurst price={p.price} priceOld={p.priceOld} accent={p.accent} fg={p.bg} size="lg" rotate={-8} />
          )}
        </div>
      </div>
    </div>
  );
}

function PromoSplit(p: PromoProps) {
  // Composição: corte diagonal 55/45, lado esquerdo com ícone gigante, direita com tipografia heroica
  return (
    <div className="w-full h-full relative overflow-hidden flex">
      {/* Coluna esquerda — bloco de cor + ícone/imagem */}
      <div
        className="relative grid place-items-center overflow-hidden"
        style={{
          width: '52%',
          backgroundColor: p.bg,
          color: p.fg,
          clipPath: 'polygon(0 0, 100% 0, calc(100% - 80px) 100%, 0 100%)',
        }}
      >
        {p.bgImage && !p.productImageUrl && !p.productEmoji ? (
          <>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${p.bgImage})` }} />
            <div className="absolute inset-0" style={{ backgroundColor: p.bg, opacity: p.overlay / 100 }} />
          </>
        ) : null}
        {/* Halo radial atrás do ícone */}
        <div
          className="absolute pointer-events-none"
          style={{
            width: '80%', height: '80%',
            background: `radial-gradient(circle, ${p.accent}40 0%, transparent 65%)`,
            filter: 'blur(20px)',
          }}
        />
        {(p.productImageUrl || p.productEmoji) ? (
          <div className="relative z-10">
            <ProductPhoto url={p.productImageUrl} emoji={p.productEmoji} size="lg" rotate={-3} />
          </div>
        ) : p.icon ? (
          <div
            className="relative leading-none"
            style={{
              fontSize: 'clamp(12rem, 28vw, 30rem)',
              filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.5))',
            }}
          >
            {p.icon}
          </div>
        ) : null}
        {p.showLogo && p.logoUrl && (
          <img src={p.logoUrl} className="absolute top-10 left-10 h-20 z-10 object-contain drop-shadow-lg" alt="" />
        )}
        <PromoNoise />
      </div>

      {/* Coluna direita — conteúdo */}
      <div
        className="relative flex items-center bg-white text-slate-900 pl-24 pr-16"
        style={{ width: '48%' }}
      >
        {/* Barra de acento vertical */}
        <div className="absolute top-12 bottom-12 left-12 w-1.5 rounded-full" style={{ backgroundColor: p.accent }} />
        <div className="relative flex flex-col gap-6 w-full">
          {p.validity && (
            <div
              className="self-start px-5 py-2 rounded-sm text-sm font-black uppercase tracking-[0.25em] shadow-md"
              style={{ backgroundColor: p.bg, color: p.fg }}
            >
              {p.validity}
            </div>
          )}
          {p.headline && (
            <h1
              className="font-black uppercase leading-[0.82] break-words"
              style={{ color: p.bg, fontSize: 'clamp(4rem, 9vw, 9rem)', letterSpacing: '-0.04em' }}
            >
              {p.headline}
            </h1>
          )}
          {p.subtitle && (
            <p className="font-semibold text-slate-600 leading-tight" style={{ fontSize: 'clamp(1.25rem, 2.2vw, 2.25rem)' }}>
              {p.subtitle}
            </p>
          )}
          {p.price && (
            <div className="flex items-baseline gap-4 flex-wrap mt-2">
              {p.priceOld && (
                <span className="text-3xl line-through text-slate-400 font-semibold tabular-nums">{p.priceOld}</span>
              )}
              <span
                className="font-black tabular-nums leading-none"
                style={{ color: p.bg, fontSize: 'clamp(4rem, 8.5vw, 8rem)', letterSpacing: '-0.04em' }}
              >
                {p.price}
              </span>
            </div>
          )}
          {p.cta && (
            <div
              className="self-start mt-3 px-8 py-4 rounded-full text-xl font-black uppercase tracking-wider shadow-xl flex items-center gap-3"
              style={{ backgroundColor: p.bg, color: p.fg, letterSpacing: '0.08em' }}
            >
              <span>{p.cta}</span>
              <span>→</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PromoUrgency(p: PromoProps) {
  // re-render a cada segundo p/ atualizar o countdown
  const [, setNow] = useState(0);
  useEffect(() => {
    if (!p.countdownTo) return;
    const t = setInterval(() => setNow(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [p.countdownTo]);

  const remaining = p.countdownTo ? Math.max(0, new Date(p.countdownTo).getTime() - Date.now()) : 0;
  const days = Math.floor(remaining / 86400000);
  const hours = Math.floor((remaining % 86400000) / 3600000);
  const minutes = Math.floor((remaining % 3600000) / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);

  return (
    <div className="w-full h-full relative overflow-hidden flex items-center justify-center px-16" style={{ color: p.fg }}>
      <PromoBg color={p.bg} image={p.bgImage} overlay={p.overlay} />
      {/* Listras diagonais sutis */}
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{ backgroundImage: `repeating-linear-gradient(45deg, transparent 0 40px, ${p.accent} 40px 80px)` }}
      />
      {/* Radial vermelho pulsante */}
      <div
        className="absolute pointer-events-none"
        style={{
          inset: 0,
          background: `radial-gradient(ellipse at center, ${p.accent}30 0%, transparent 60%)`,
          animation: 'weather-pulse 3s ease-in-out infinite',
        }}
      />
      <PromoNoise />
      {p.showLogo && <PromoLogo url={p.logoUrl} />}

      {/* Com foto (ou produto sem imagem → emoji): layout horizontal */}
      {(p.productImageUrl || p.productEmoji) && (
        <div className="relative z-10 flex items-center gap-16 w-full max-w-[95%]">
          <div className="shrink-0">
            <ProductPhoto url={p.productImageUrl} emoji={p.productEmoji} size="lg" rotate={-4} />
          </div>
          <div className="flex-1 flex flex-col gap-6 text-center">
            <div
              className="px-8 py-3 rounded-full text-xl font-black uppercase tracking-[0.3em] animate-pulse shadow-2xl flex items-center justify-center gap-3 self-center"
              style={{ backgroundColor: p.accent, color: p.bg, boxShadow: `0 0 60px ${p.accent}80` }}
            >
              <span className="text-2xl">{p.icon || '⏰'}</span>
              <span>Por tempo limitado</span>
            </div>
            {p.headline && (
              <h1 className="font-black uppercase leading-[0.82]" style={{ fontSize: 'clamp(3rem, 7vw, 8rem)', letterSpacing: '-0.045em' }}>
                {p.headline}
              </h1>
            )}
            {p.subtitle && <p className="font-semibold opacity-95 leading-tight" style={{ fontSize: 'clamp(1.25rem, 2.2vw, 2.25rem)' }}>{p.subtitle}</p>}
            {p.price && (
              <div className="flex items-baseline gap-4 justify-center">
                {p.priceOld && <span className="text-3xl line-through opacity-60 font-bold tabular-nums">{p.priceOld}</span>}
                <span className="font-black tabular-nums leading-none" style={{ color: p.accent, fontSize: 'clamp(4rem, 9vw, 9rem)', letterSpacing: '-0.04em' }}>{p.price}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sem foto nem emoji: layout vertical centrado original */}
      {!p.productImageUrl && !p.productEmoji && (
        <div className="relative z-10 flex flex-col items-center gap-7 max-w-[92%] text-center">
          <div
            className="px-8 py-3 rounded-full text-xl font-black uppercase tracking-[0.3em] animate-pulse shadow-2xl flex items-center gap-3"
            style={{
              backgroundColor: p.accent,
              color: p.bg,
              boxShadow: `0 0 60px ${p.accent}80`,
            }}
          >
            <span className="text-2xl">{p.icon || '⏰'}</span>
            <span>Por tempo limitado</span>
          </div>

          {p.headline && (
            <h1
              className="font-black uppercase leading-[0.82] break-words"
              style={{
                fontSize: 'clamp(5rem, 11vw, 12rem)',
                letterSpacing: '-0.045em',
                textShadow: `0 0 40px ${p.accent}60, 0 12px 30px rgba(0,0,0,0.5)`,
              }}
            >
              {p.headline}
            </h1>
          )}

          {p.countdownTo && (
            <div className="flex gap-4">
              {([
                ['DIAS', days],
                ['HORAS', hours],
                ['MIN', minutes],
                ['SEG', seconds],
              ] as const).map(([lbl, n]) => (
                <div
                  key={lbl}
                  className="relative rounded-2xl px-7 py-5 min-w-[150px] border"
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.55)',
                    borderColor: `${p.accent}50`,
                    boxShadow: `0 20px 60px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)`,
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <div
                    className="text-8xl font-black tabular-nums leading-none"
                    style={{ color: p.accent, letterSpacing: '-0.04em' }}
                  >
                    {String(n).padStart(2, '0')}
                  </div>
                  <div className="text-xs font-black tracking-[0.35em] opacity-70 mt-2.5">{lbl}</div>
                </div>
              ))}
            </div>
          )}

          {p.subtitle && (
            <p
              className="font-semibold opacity-95 max-w-5xl leading-tight"
              style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2.5rem)' }}
            >
              {p.subtitle}
            </p>
          )}

          {p.price && (
            <div className="flex items-baseline gap-5">
              {p.priceOld && (
                <span className="text-4xl line-through opacity-60 font-bold tabular-nums">{p.priceOld}</span>
              )}
              <span
                className="font-black tabular-nums leading-none"
                style={{
                  color: p.accent,
                  fontSize: 'clamp(5rem, 11vw, 11rem)',
                  letterSpacing: '-0.04em',
                  textShadow: `0 0 50px ${p.accent}70`,
                }}
              >
                {p.price}
              </span>
            </div>
          )}

          {p.cta && (
            <div
              className="mt-2 px-12 py-5 rounded-full text-2xl font-black uppercase tracking-[0.15em] shadow-2xl"
              style={{
                backgroundColor: p.accent,
                color: p.bg,
                boxShadow: `0 25px 60px -10px ${p.accent}90`,
              }}
            >
              {p.cta} →
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PromoMinimal(p: PromoProps) {
  // Editorial: tipografia leve, composição em proporção áurea, geometria sutil
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ color: p.fg, backgroundColor: p.bg }}>
      {p.bgImage && (
        <>
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${p.bgImage})` }} />
          <div className="absolute inset-0" style={{ backgroundColor: p.bg, opacity: p.overlay / 100 }} />
        </>
      )}
      <PromoNoise />

      {/* Crosshair geométrico nos cantos — detalhe editorial */}
      {(['top-12 left-12', 'top-12 right-12', 'bottom-12 left-12', 'bottom-12 right-12'] as const).map((pos, i) => (
        <div key={i} className={`absolute ${pos} pointer-events-none`}>
          <div className="w-8 h-px" style={{ backgroundColor: p.accent }} />
          <div className="w-px h-8" style={{ backgroundColor: p.accent }} />
        </div>
      ))}

      {p.showLogo && <PromoLogo url={p.logoUrl} />}

      {/* Validade alinhada à esquerda — estilo editorial */}
      {p.validity && (
        <div
          className="absolute top-1/2 left-12 -translate-y-1/2 origin-center font-bold uppercase opacity-60"
          style={{
            transform: 'translateY(-50%) rotate(-90deg)',
            transformOrigin: 'left center',
            fontSize: '0.875rem',
            letterSpacing: '0.5em',
            whiteSpace: 'nowrap',
          }}
        >
          {p.validity}
        </div>
      )}

      <div className="relative z-10 w-full h-full grid grid-cols-12 items-center px-24">
        <div className="col-span-7 col-start-2 flex flex-col gap-8">
          {p.icon && (
            <div className="text-8xl opacity-90 leading-none -mb-2" style={{ filter: 'drop-shadow(0 8px 20px rgba(0,0,0,0.3))' }}>
              {p.icon}
            </div>
          )}
          <div className="h-px w-24" style={{ backgroundColor: p.accent }} />
          {p.headline && (
            <h1
              className="font-extralight uppercase leading-[0.95]"
              style={{
                fontSize: 'clamp(4rem, 9vw, 9rem)',
                letterSpacing: '0.04em',
              }}
            >
              {p.headline}
            </h1>
          )}
          {p.subtitle && (
            <p className="font-light opacity-80 max-w-2xl leading-snug" style={{ fontSize: 'clamp(1.25rem, 2vw, 1.875rem)' }}>
              {p.subtitle}
            </p>
          )}
        </div>

        {/* Coluna direita: foto do produto (se houver) + preço; senão só preço */}
        <div className="col-span-4 col-start-9 flex flex-col items-end gap-4">
          {(p.productImageUrl || p.productEmoji) ? (
            <div className="flex flex-col items-end gap-4">
              <ProductPhoto url={p.productImageUrl} emoji={p.productEmoji} size="md" rotate={2} />
              {p.priceOld && <span className="text-2xl line-through opacity-50 font-light tabular-nums">{p.priceOld}</span>}
              {p.price && (
                <span className="font-black tabular-nums leading-none text-right" style={{ color: p.accent, fontSize: 'clamp(3rem, 7vw, 7rem)', letterSpacing: '-0.04em' }}>
                  {p.price}
                </span>
              )}
            </div>
          ) : (
            <>
              {p.priceOld && <span className="text-2xl line-through opacity-50 font-light tabular-nums">{p.priceOld}</span>}
              {p.price && (
                <span className="font-extralight tabular-nums leading-none text-right" style={{ color: p.accent, fontSize: 'clamp(4rem, 9vw, 9rem)', letterSpacing: '-0.04em' }}>
                  {p.price}
                </span>
              )}
            </>
          )}
          {p.cta && (
            <div
              className="mt-6 px-7 py-3 text-base font-medium uppercase border-b-2 inline-flex items-center gap-2"
              style={{ borderColor: p.accent, color: p.fg, letterSpacing: '0.3em' }}
            >
              <span>{p.cta}</span>
              <span style={{ color: p.accent }}>→</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PromoBold(p: PromoProps) {
  // IMPACTO: tudo no máximo. Headline ocupa metade da tela, price burst sobrepondo, CTA full-bleed no rodapé.
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ color: p.fg }}>
      <PromoBg color={p.bg} image={p.bgImage} overlay={p.overlay} />

      {/* Blob radial grande no canto pra dar profundidade */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: '-25%',
          right: '-15%',
          width: '90vh',
          height: '90vh',
          background: `radial-gradient(circle, ${p.accent}40 0%, transparent 60%)`,
          filter: 'blur(40px)',
        }}
      />
      {/* Faixas diagonais decorativas — agora mais sutis e largas */}
      <div
        className="absolute"
        style={{
          top: '-15%',
          left: '-10%',
          width: '70%',
          height: '120px',
          backgroundColor: p.accent,
          transform: 'rotate(-8deg)',
          opacity: 0.9,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      />
      <PromoNoise />

      {p.showLogo && <PromoLogo url={p.logoUrl} />}

      {/* Ribbon de validade vindo do topo direito */}
      {p.validity && (
        <div
          className="absolute top-10 right-10 px-7 py-3 text-sm font-black uppercase tracking-[0.3em] shadow-2xl z-20"
          style={{
            backgroundColor: p.accent,
            color: p.bg,
            transform: 'rotate(3deg)',
            boxShadow: `0 20px 40px ${p.accent}80`,
          }}
        >
          {p.validity}
        </div>
      )}

      {/* Conteúdo principal — headline gigante + price burst sobrepondo */}
      <div className="relative z-10 w-full h-full flex flex-col justify-center px-16 pb-32">
        <div className="relative flex items-center min-w-0">
          <div className="flex-1 min-w-0 relative">
            {p.headline && (
              <>
                {/* Headline "stroke" atrás pra efeito de profundidade */}
                <h1
                  aria-hidden
                  className="absolute inset-0 font-black uppercase leading-[0.78] break-words pointer-events-none select-none"
                  style={{
                    fontSize: 'clamp(6rem, 18vw, 22rem)',
                    letterSpacing: '-0.05em',
                    WebkitTextStroke: `2px ${p.accent}`,
                    color: 'transparent',
                    transform: 'translate(14px, 14px)',
                    opacity: 0.4,
                  }}
                >
                  {p.headline}
                </h1>
                {/* Headline filled na frente */}
                <h1
                  className="relative font-black uppercase leading-[0.78] break-words"
                  style={{
                    fontSize: 'clamp(6rem, 18vw, 22rem)',
                    letterSpacing: '-0.05em',
                    textShadow: '0 20px 50px rgba(0,0,0,0.5)',
                  }}
                >
                  {p.headline}
                </h1>
              </>
            )}
            {p.subtitle && (
              <p
                className="font-bold mt-6 opacity-95 leading-tight max-w-[88%]"
                style={{ fontSize: 'clamp(1.75rem, 3vw, 3rem)', letterSpacing: '-0.01em' }}
              >
                {p.subtitle}
              </p>
            )}
          </div>
          {/* Foto do produto em destaque (se houver) + price burst sobreposto; senão só price burst */}
          {(p.productImageUrl || p.productEmoji) ? (
            <div className="shrink-0 -mr-8 relative">
              <ProductPhoto url={p.productImageUrl} emoji={p.productEmoji} size="lg" rotate={-5} />
              {p.price && (
                <div className="absolute -bottom-8 -left-14">
                  <PriceBurst price={p.price} priceOld={p.priceOld} accent={p.accent} fg={p.bg} size="md" rotate={-12} />
                </div>
              )}
            </div>
          ) : p.price ? (
            <div className="shrink-0 -mr-12 -mt-8 relative">
              <PriceBurst price={p.price} priceOld={p.priceOld} accent={p.accent} fg={p.bg} size="lg" rotate={-10} />
            </div>
          ) : null}
        </div>
      </div>

      {/* CTA bar full-bleed no rodapé */}
      {p.cta && (
        <div
          className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-between px-16 py-7"
          style={{
            backgroundColor: p.fg,
            color: p.bg,
            boxShadow: '0 -20px 50px rgba(0,0,0,0.3)',
          }}
        >
          <div className="flex items-center gap-4">
            {p.icon && <span className="text-5xl leading-none">{p.icon}</span>}
            <span
              className="font-black uppercase"
              style={{ fontSize: 'clamp(1.75rem, 2.8vw, 2.75rem)', letterSpacing: '0.06em' }}
            >
              {p.cta}
            </span>
          </div>
          <span className="text-5xl" style={{ color: p.accent }}>→</span>
        </div>
      )}
    </div>
  );
}

/** Separa o símbolo de moeda do valor ("R$ 34,90" → {cur:"R$", num:"34,90"}) p/ renderizar o R$ pequeno. */
function splitPrice(raw: string): { cur: string; num: string } {
  const m = String(raw ?? '').match(/^\s*(R\$|US\$|\$|€)?\s*(.*)$/);
  return { cur: m?.[1] ?? '', num: (m?.[2] || raw) ?? '' };
}

function PromoVitrine(p: PromoProps) {
  // VITRINE: foto do produto sem moldura (look de cartaz de estúdio) à direita,
  // degradê do fundo subindo da esquerda/baixo pra dar legibilidade ao texto empilhado no canto inferior esquerdo.
  const { cur, num } = splitPrice(p.price);
  const hasProduct = !!(p.productImageUrl || p.productEmoji);
  return (
    <div className="w-full h-full relative overflow-hidden" style={{ color: p.fg }}>
      <PromoBg color={p.bg} image={p.bgImage} overlay={p.overlay} />

      {/* Foto do produto em destaque à direita — sem moldura, sombra cinematográfica */}
      {hasProduct && (
        <div className="absolute inset-y-0 right-0 w-[58%] grid place-items-center pr-4">
          {p.productImageUrl ? (
            <img
              src={p.productImageUrl}
              alt=""
              className="max-w-full max-h-[88%] object-contain"
              style={{ filter: 'drop-shadow(0 40px 70px rgba(0,0,0,0.6))' }}
            />
          ) : (
            <span className="text-[26rem] leading-none" style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.5))' }}>
              {p.productEmoji}
            </span>
          )}
        </div>
      )}

      {/* Degradê do fundo: sobe da base e entra pela esquerda — garante leitura do texto sobre qualquer imagem */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `linear-gradient(to top, ${p.bg} 4%, ${p.bg}00 48%), linear-gradient(105deg, ${p.bg} 14%, ${p.bg}cc 34%, ${p.bg}00 62%)`,
        }}
      />
      <PromoNoise />
      {p.showLogo && <PromoLogo url={p.logoUrl} />}

      {/* Ribbon de validade — topo direito */}
      {p.validity && (
        <div
          className="absolute top-10 right-10 px-7 py-3 text-sm font-black uppercase tracking-[0.3em] shadow-2xl z-20"
          style={{ backgroundColor: p.accent, color: p.bg, boxShadow: `0 20px 40px ${p.accent}80` }}
        >
          {p.validity}
        </div>
      )}

      {/* Bloco de texto no canto inferior esquerdo */}
      <div className="absolute bottom-0 left-0 z-10 flex flex-col px-16 pb-14 max-w-[62%]">
        {p.headline && (
          <h1
            className="font-black uppercase leading-[0.85] break-words drop-shadow-2xl"
            style={{
              fontSize: 'clamp(4rem, 9vw, 10rem)',
              letterSpacing: '-0.04em',
              textShadow: '0 12px 40px rgba(0,0,0,0.5)',
            }}
          >
            {p.headline}
          </h1>
        )}
        {p.subtitle && (
          <p
            className="font-semibold mt-4 opacity-90 leading-tight"
            style={{ fontSize: 'clamp(1.5rem, 2.4vw, 2.5rem)', letterSpacing: '-0.005em' }}
          >
            {p.subtitle}
          </p>
        )}
        {p.price && (
          <div className="flex items-end gap-5 mt-6">
            <div className="flex items-end leading-none" style={{ color: p.accent }}>
              {cur && (
                <span
                  className="font-black self-start mt-2"
                  style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2.75rem)', marginRight: '0.15em' }}
                >
                  {cur}
                </span>
              )}
              <span
                className="font-black"
                style={{
                  fontSize: 'clamp(4.5rem, 11vw, 12rem)',
                  letterSpacing: '-0.04em',
                  textShadow: '0 14px 40px rgba(0,0,0,0.45)',
                }}
              >
                {num}
              </span>
            </div>
            {p.priceOld && (
              <span className="font-bold line-through opacity-60 pb-4" style={{ fontSize: 'clamp(1.25rem, 2vw, 2rem)' }}>
                {p.priceOld}
              </span>
            )}
          </div>
        )}
        {p.cta && (
          <div
            className="self-start mt-7 px-9 py-4 rounded-full text-xl font-black uppercase shadow-2xl"
            style={{ backgroundColor: p.accent, color: p.bg, letterSpacing: '0.06em' }}
          >
            {p.cta}
          </div>
        )}
      </div>
    </div>
  );
}

function DateTimeSlide({ content, branding }: any) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const showSeconds = content.show_seconds !== false;
  const showDate = content.show_date !== false;
  const showLogo = content.show_logo === true;
  const logoUrl = content.logo_source === 'custom' ? (content.logo_url || null) : (branding?.logo_url || null);

  const time = now.toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit',
    ...(showSeconds ? { second: '2-digit' } : {}),
  } as any);
  const date = now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });

  const THEMES: Record<string, { bg: string; timeColor: string; dateColor: string; logoBg: string; divider: string }> = {
    dark:  { bg: '#0f172a', timeColor: '#ffffff', dateColor: '#94a3b8',               logoBg: 'rgba(255,255,255,0.07)', divider: 'rgba(255,255,255,0.12)' },
    light: { bg: '#f8fafc', timeColor: '#1e293b', dateColor: '#64748b',               logoBg: 'rgba(0,0,0,0.05)',       divider: 'rgba(0,0,0,0.10)' },
    glass: { bg: 'linear-gradient(135deg,#1e293b,#0f172a)', timeColor: '#ffffff', dateColor: 'rgba(255,255,255,0.55)', logoBg: 'rgba(255,255,255,0.10)', divider: 'rgba(255,255,255,0.15)' },
    brand: { bg: 'linear-gradient(135deg,#7c3aed,#4f46e5)', timeColor: '#ffffff', dateColor: 'rgba(255,255,255,0.75)', logoBg: 'rgba(255,255,255,0.15)', divider: 'rgba(255,255,255,0.22)' },
  };
  const t = THEMES[content.theme || 'dark'] ?? THEMES.dark;

  const isLarge   = !content.style || content.style === 'large';
  const isMinimal = content.style === 'minimal';
  const timeSize  = isLarge ? '14vw' : isMinimal ? '7vw' : '10vw';

  return (
    <div className="w-full h-full flex flex-col items-center justify-center" style={{ background: t.bg, gap: '3.5vh' }}>
      {showLogo && logoUrl && (
        <>
          <div
            style={{
              background: t.logoBg,
              backdropFilter: 'blur(12px)',
              borderRadius: '1.5vw',
              padding: '2.5vh 4vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={logoUrl}
              alt="Logo"
              style={{ maxHeight: '14vh', maxWidth: '42vw', objectFit: 'contain' }}
            />
          </div>
          <div style={{ width: '18vw', height: '1px', background: t.divider }} />
        </>
      )}
      <div style={{ fontSize: timeSize, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, color: t.timeColor, fontVariantNumeric: 'tabular-nums' }}>
        {time}
      </div>
      {showDate && !isMinimal && (
        <div style={{ fontSize: '2.6vw', color: t.dateColor, textTransform: 'capitalize', letterSpacing: '0.04em', fontWeight: 400 }}>
          {date}
        </div>
      )}
    </div>
  );
}

// ===================== Futebol / Brasileirão (preview) =====================

interface FbTeam { name: string; short_name?: string | null; crest?: string | null; score?: number | null; }
interface FbMatch {
  id: number; status: string; stage?: string | null; group?: string | null;
  utc_date?: string | null; minute?: string | null; home: FbTeam; away: FbTeam;
}
interface FbStandingRow {
  pos: number; team?: string | null; short_name?: string | null; crest?: string | null;
  played: number; won: number; draw: number; lost: number; gf: number; ga: number; gd: number; points: number;
}
interface FbGroup { group?: string | null; rows: FbStandingRow[]; }
interface FbBracketStage { stage: string; matches: FbMatch[]; }
interface FootballData {
  competition: string;
  live: FbMatch[]; today: FbMatch[]; yesterday: FbMatch[]; upcoming: FbMatch[];
  standings: FbGroup[]; bracket: FbBracketStage[];
}

const FB_THEME_BG: Record<string, string> = {
  dark: 'linear-gradient(135deg,#0b1220,#111c33)',
  green: 'linear-gradient(135deg,#064e3b,#065f46)',
  brand: 'linear-gradient(135deg,#4f46e5,#7c3aed)',
};

function fbIsLive(status: string) { return status === 'IN_PLAY' || status === 'PAUSED'; }

// Bandeira do Brasil estilizada (fumaça sobre preto) — charme de fundo.
const FB_BR_FLAG = 'https://pub-08f9276e8ae047b88d377ebf8e6accb3.r2.dev/MyShow/brasil-fundo.jpg';

// "Charme" do Brasil só quando o produto roda no Brasil. Detecta pelo fuso
// (zonas IANA do Brasil); fallback pro offset UTC-3.
const FB_BR_TIMEZONES = new Set([
  'America/Sao_Paulo', 'America/Bahia', 'America/Fortaleza', 'America/Recife',
  'America/Belem', 'America/Manaus', 'America/Cuiaba', 'America/Campo_Grande',
  'America/Boa_Vista', 'America/Porto_Velho', 'America/Rio_Branco', 'America/Maceio',
  'America/Araguaina', 'America/Santarem', 'America/Eirunepe', 'America/Noronha',
]);
function fbIsBrazil(): boolean {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) return FB_BR_TIMEZONES.has(tz);
  } catch { /* sem Intl tz */ }
  return new Date().getTimezoneOffset() === 180;
}

function fbMatchTime(m: FbMatch): string {
  if (fbIsLive(m.status)) return m.minute ? `${m.minute}'` : 'AO VIVO';
  if (m.status === 'FINISHED' || m.status === 'AWARDED') return 'Encerrado';
  if (m.utc_date) {
    const d = new Date(m.utc_date);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return '';
}

function fbClockTime(m: FbMatch): string {
  if (!m.utc_date) return '';
  return new Date(m.utc_date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function fbMatchDate(m: FbMatch): string {
  if (!m.utc_date) return '';
  const d = new Date(m.utc_date);
  return `${WEEKDAYS_PT_PREVIEW[d.getDay()]} ${d.getDate()}/${MONTHS_PT_PREVIEW[d.getMonth()]}`;
}

// `base` = menor dimensão do slide (igual ao tv-app). Todo tamanho é uma
// fração de `base`, então o preview no navegador fica proporcional e IDÊNTICO
// à TV, independente da resolução. Medido por ResizeObserver no root.
const FbBoxCtx = createContext<{ base: number; landscape: boolean }>({ base: 600, landscape: true });
function useFbBox() {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ base: 600, landscape: true });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setBox({ base: Math.min(r.width, r.height), landscape: r.width >= r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, box };
}

// Nome longo vira a sigla (TLA) pra nunca cortar — regra DETERMINÍSTICA e igual
// na TV: ex. "Bosnia-Herzegovina" → "BIH", "United States" → "USA".
function fbDisplayName(t: FbTeam): string {
  const n = t.name || '';
  return n.length <= 12 ? n : (t.short_name || n);
}

function FbCrest({ team, size = 40 }: { team: FbTeam; size?: number }) {
  const [ok, setOk] = useState(true);
  if (team.crest && ok) {
    return <img src={team.crest} alt="" width={size} height={size} style={{ objectFit: 'contain' }} onError={() => setOk(false)} />;
  }
  return <span style={{ fontSize: size * 0.7, lineHeight: 1 }}>🏳️</span>;
}

function FbMatchRow({ m, big, withDate, grow, fit = 1 }: { m: FbMatch; big?: boolean; withDate?: boolean; grow?: boolean; fit?: number }) {
  const { base } = useContext(FbBoxCtx);
  const live = fbIsLive(m.status);
  const finished = m.status === 'FINISHED' || m.status === 'AWARDED';
  const hasScore = m.home.score != null && m.away.score != null;
  // Frações de `base` IDÊNTICAS ao tv-app (FootballSlide MatchRow).
  const px = (r: number) => Math.round(base * r);
  const crestSize = big ? px(0.105 * fit) : px(0.05);
  const nameSz = big ? px(0.046 * fit) : px(0.03);
  const scoreSz = big ? px(0.12 * fit) : px(0.045);
  const timeSz = big ? px(0.09 * fit) : px(0.032);
  const dateSz = big ? px(0.03 * fit) : px(0.024);
  const subSz = big ? px(0.026 * fit) : px(0.022);
  const gap = big ? px(0.025) : 8;
  const dot = big ? px(0.018) : 6;
  return (
    <div className={`flex items-center w-full rounded-xl ${grow ? 'flex-1' : ''}`}
         style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.10)', paddingBlock: big ? px(0.014) : 10, paddingInline: big ? px(0.025) : 16 }}>
      <div className="flex items-center flex-1 min-w-0 text-right justify-end" style={{ gap }}>
        <span className="font-semibold truncate" style={{ fontSize: nameSz }}>{fbDisplayName(m.home)}</span>
        <FbCrest team={m.home} size={crestSize} />
      </div>
      <div className="shrink-0 text-center flex flex-col items-center justify-center" style={{ minWidth: big ? px(0.24) : 96, paddingInline: big ? px(0.03) : 16, gap: big ? px(0.008) : 2 }}>
        {hasScore ? (
          <div className="font-black tabular-nums leading-none" style={{ fontSize: scoreSz }}>{m.home.score} <span className="opacity-40">×</span> {m.away.score}</div>
        ) : withDate ? (
          <>
            <div className="font-medium uppercase tracking-wider opacity-60" style={{ fontSize: dateSz }}>{fbMatchDate(m)}</div>
            <div className="font-black tabular-nums leading-none" style={{ fontSize: timeSz }}>{fbClockTime(m)}</div>
          </>
        ) : (
          <div className="font-black tabular-nums leading-none" style={{ fontSize: timeSz }}>{fbMatchTime(m)}</div>
        )}
        {(live || finished) && (
          <div className={`font-bold uppercase tracking-wider ${live ? 'text-red-400' : 'opacity-55'}`} style={{ fontSize: subSz }}>
            {live ? <span className="inline-flex items-center gap-1.5"><span className="inline-block rounded-full bg-red-500 animate-pulse" style={{ width: dot, height: dot }} />{m.minute ? `${m.minute}'` : 'AO VIVO'}</span>
              : 'Encerrado'}
          </div>
        )}
      </div>
      <div className="flex items-center flex-1 min-w-0 justify-start" style={{ gap }}>
        <FbCrest team={m.away} size={crestSize} />
        <span className="font-semibold truncate" style={{ fontSize: nameSz }}>{fbDisplayName(m.away)}</span>
      </div>
    </div>
  );
}

function FbPanelTitle({ children }: { children: React.ReactNode }) {
  const { base } = useContext(FbBoxCtx);
  return <div className="font-extrabold tracking-tight flex items-center gap-2" style={{ fontSize: Math.round(base * 0.05), marginBottom: Math.round(base * 0.022) }}>{children}</div>;
}

function FbEmpty({ label }: { label: string }) {
  return <div className="flex-1 grid place-items-center text-center opacity-70"><div><div className="text-5xl mb-2">⚽</div><div className="text-lg">{label}</div></div></div>;
}

function FbMatchListPanel({ title, matches, empty, big, withDate }: { title: React.ReactNode; matches: FbMatch[]; empty: string; big?: boolean; withDate?: boolean }) {
  const limit = big ? 4 : 6;
  const rows = matches.slice(0, limit);
  const fit = rows.length <= 2 ? 1.1 : rows.length === 3 ? 0.92 : 0.7;
  return (
    <div className="flex flex-col h-full">
      <FbPanelTitle>{title}</FbPanelTitle>
      {rows.length === 0 ? <FbEmpty label={empty} /> : (
        <div className={`flex flex-col overflow-hidden ${big ? 'gap-4 flex-1' : 'gap-2'}`}>{rows.map(m => <FbMatchRow key={m.id} m={m} big={big} withDate={withDate} grow={big} fit={fit} />)}</div>
      )}
    </div>
  );
}

// Zonas da Série A: G4 Libertadores (verde) e Z4 rebaixamento (vermelho).
function fbZoneBg(pos: number, total: number): string {
  if (pos <= 4) return 'rgba(34,197,94,0.18)';
  if (pos > total - 4) return 'rgba(239,68,68,0.16)';
  return 'rgba(255,255,255,0.06)';
}

function FbStandingsTable({ rows, total, scale }: { rows: FbStandingRow[]; total: number; scale: number }) {
  const { base } = useContext(FbBoxCtx);
  const cell = Math.round(base * 0.03 * scale);
  const crest = Math.round(base * 0.032 * scale);
  const cols = `${cell * 1.4}px 1fr ${cell * 1.4}px ${cell * 1.8}px ${cell * 2}px`;
  return (
    <div className="flex flex-col h-full">
      <div className="grid items-center uppercase tracking-wide opacity-60" style={{ gridTemplateColumns: cols, fontSize: Math.round(cell * 0.78), paddingInline: 6 }}>
        <span>#</span><span>Time</span><span className="text-center">J</span><span className="text-center">SG</span><span className="text-center">Pts</span>
      </div>
      <div className="flex flex-col flex-1" style={{ gap: Math.round(base * 0.006) }}>
        {rows.map(r => (
          <div key={r.pos} className="grid items-center rounded-lg flex-1" style={{ gridTemplateColumns: cols, fontSize: cell, paddingInline: 6, background: fbZoneBg(r.pos, total) }}>
            <span className="font-bold">{r.pos}</span>
            <span className="flex items-center min-w-0" style={{ gap: Math.round(cell * 0.4) }}>
              {r.crest && <img src={r.crest} alt="" style={{ width: crest, height: crest, objectFit: 'contain' }} />}
              <span className="font-semibold truncate">{fbDisplayName({ name: r.team || '', short_name: r.short_name })}</span>
            </span>
            <span className="text-center">{r.played}</span>
            <span className="text-center">{r.gd > 0 ? `+${r.gd}` : r.gd}</span>
            <span className="text-center font-black">{r.points}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FbStandingsPanel({ groups }: { groups: FbGroup[] }) {
  const { base, landscape } = useContext(FbBoxCtx);
  // Série A: tabela única de 20 times (standings vem com um só "grupo" TOTAL).
  const rows = groups[0]?.rows ?? [];
  if (rows.length === 0) return <FbEmpty label="Tabela indisponível no momento" />;
  // Landscape divide em 2 colunas de 10; portrait usa coluna única, menor.
  const chunks = landscape ? [rows.slice(0, 10), rows.slice(10)].filter(c => c.length) : [rows];
  const scale = landscape ? 1 : 0.75;
  return (
    <div className="flex flex-col h-full">
      <FbPanelTitle>📊 Classificação</FbPanelTitle>
      <div className="grid flex-1" style={{ gridTemplateColumns: `repeat(${chunks.length}, minmax(0, 1fr))`, columnGap: Math.round(base * 0.04) }}>
        {chunks.map((c, i) => <FbStandingsTable key={i} rows={c} total={rows.length} scale={scale} />)}
      </div>
    </div>
  );
}

async function fetchFootballPreview(): Promise<FootballData | null> {
  try {
    const r = await api.get('/api/signage/football');
    return r.data as FootballData;
  } catch {
    return null;
  }
}

function FootballSlide({ content }: any) {
  const view: string = content?.view || 'rotate';
  const theme: string = content?.theme || 'dark';
  const showClock = content?.show_clock !== false;
  const [data, setData] = useState<FootballData | null>(null);
  const [error, setError] = useState(false);
  const [panelIdx, setPanelIdx] = useState(0);
  const { ref, box } = useFbBox();

  useEffect(() => {
    let active = true;
    setError(false);
    // Sempre consulta o servidor; revalida a cada 30s enquanto aberto pra
    // placar ao vivo atualizar. Em refresh, mantém o último dado se falhar.
    const load = async (initial: boolean) => {
      const d = await fetchFootballPreview();
      if (!active) return;
      if (d) { setData(d); setError(false); }
      else if (initial) setError(true);
    };
    load(true);
    const t = setInterval(() => load(false), 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  // Painéis disponíveis (com dados) para o modo "rotate".
  const panels = useMemo(() => {
    if (!data) return [] as string[];
    const p: string[] = [];
    if (data.live.length || data.today.length) p.push('live_today');
    if (data.yesterday.length) p.push('results');
    if (data.standings.length) p.push('standings');
    if (data.upcoming.length) p.push('upcoming');
    return p.length ? p : ['upcoming'];
  }, [data]);

  useEffect(() => {
    if (view !== 'rotate' || panels.length <= 1) return;
    const t = setInterval(() => setPanelIdx(i => (i + 1) % panels.length), 6000);
    return () => clearInterval(t);
  }, [view, panels.length]);

  const bg = FB_THEME_BG[theme] || FB_THEME_BG.dark;

  if (error) {
    return (
      <div className="w-full h-full grid place-items-center text-white" style={{ background: bg }}>
        <div className="text-center"><div className="text-7xl">⚽</div>
          <div className="text-2xl mt-3 opacity-90">Dados de futebol indisponíveis</div>
          <div className="text-base mt-1 opacity-60">Verifique o token FOOTBALL_API_TOKEN no servidor</div></div>
      </div>
    );
  }
  if (!data) {
    return <div className="w-full h-full grid place-items-center text-white" style={{ background: bg }}><div className="text-2xl opacity-70">Carregando dados do campeonato…</div></div>;
  }

  const active = view === 'rotate' ? panels[Math.min(panelIdx, panels.length - 1)] : view;

  let panel: React.ReactNode;
  switch (active) {
    case 'results':
      panel = <FbMatchListPanel title={<>📅 Resultados de ontem</>} matches={data.yesterday} empty="Sem jogos ontem" big />; break;
    case 'standings':
      panel = <FbStandingsPanel groups={data.standings} />; break;
    case 'upcoming':
      panel = <FbMatchListPanel title={<>⏭️ Próximos jogos</>} matches={data.upcoming} empty="Sem jogos agendados" big withDate />; break;
    case 'live_today':
    default: {
      const live = data.live.length ? data.live : data.today;
      const title = data.live.length
        ? <><span className="inline-block w-3 h-3 rounded-full bg-red-500 animate-pulse" /> Ao vivo</>
        : <>📺 Jogos de hoje</>;
      panel = <FbMatchListPanel title={title} matches={live} empty="Nenhum jogo hoje" big />;
    }
  }

  return (
    <FbBoxCtx.Provider value={box}>
      <div ref={ref} className="w-full h-full flex flex-col text-white relative overflow-hidden" style={{ background: bg, padding: Math.round(box.base * 0.04) }}>
        {fbIsBrazil() && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            <img
              src={FB_BR_FLAG}
              alt=""
              className="fb-flag-bg absolute inset-0 w-full h-full"
              style={{ objectFit: 'cover', opacity: 0.42, filter: 'brightness(0.75) saturate(1.1)' }}
            />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,13,28,0.55) 0%, rgba(8,13,28,0.4) 50%, rgba(8,13,28,0.6) 100%)' }} />
          </div>
        )}
        <div className="relative z-10 flex items-center justify-between" style={{ marginBottom: Math.round(box.base * 0.03) }}>
          <div className="flex items-center gap-2 font-black tracking-tight" style={{ fontSize: Math.round(box.base * 0.05) }}>🏆 {data.competition}</div>
          {showClock && <div className="scale-[0.5] origin-right -my-6"><LiveClockWeb /></div>}
        </div>
        <div className="relative z-10 flex-1 min-h-0">{panel}</div>
      </div>
    </FbBoxCtx.Provider>
  );
}

interface WeatherDaily { date: string; weather_code: number; temp_max: number; temp_min: number; }
interface WeatherInfo { city: string; temp: number; weather_code: number; daily: WeatherDaily[]; }

const WEEKDAYS_PT_PREVIEW = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS_PT_PREVIEW = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

function pad2Preview(n: number): string { return n < 10 ? '0' + n : String(n); }

function LiveClockWeb() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = pad2Preview(now.getHours());
  const mm = pad2Preview(now.getMinutes());
  const ss = pad2Preview(now.getSeconds());
  const weekday = WEEKDAYS_PT_PREVIEW[now.getDay()];
  const day = now.getDate();
  const month = MONTHS_PT_PREVIEW[now.getMonth()];
  return (
    <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 2 }}>
        <span
          style={{
            color: '#fff',
            fontSize: 'clamp(2rem, 6vw, 6rem)',
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            textShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          {hh}:{mm}
        </span>
        <span
          style={{
            color: '#fff',
            fontSize: 'clamp(1.2rem, 3.5vw, 3.5rem)',
            fontWeight: 500,
            opacity: 0.7,
            marginBottom: '0.15em',
            marginLeft: '0.1em',
            textShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
        >
          :{ss}
        </span>
      </div>
      <div
        style={{
          color: '#fff',
          fontSize: 'clamp(0.75rem, 2vw, 1.75rem)',
          fontWeight: 500,
          opacity: 0.8,
          letterSpacing: '0.03em',
          marginTop: '0.15rem',
          textShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }}
      >
        {weekday}, {day} {month}
      </div>
    </div>
  );
}

function describeWeatherPreview(code: number): { emoji: string; desc: string } {
  if (code === 0) return { emoji: '☀️', desc: 'Céu limpo' };
  if (code === 1) return { emoji: '🌤️', desc: 'Predominantemente claro' };
  if (code === 2) return { emoji: '⛅', desc: 'Parcialmente nublado' };
  if (code === 3) return { emoji: '☁️', desc: 'Nublado' };
  if (code === 45 || code === 48) return { emoji: '🌫️', desc: 'Neblina' };
  if (code >= 51 && code <= 57) return { emoji: '🌦️', desc: 'Garoa' };
  if (code >= 61 && code <= 65) return { emoji: '🌧️', desc: 'Chuva' };
  if (code >= 66 && code <= 67) return { emoji: '🌧️', desc: 'Chuva congelante' };
  if (code >= 71 && code <= 77) return { emoji: '❄️', desc: 'Neve' };
  if (code >= 80 && code <= 82) return { emoji: '🌧️', desc: 'Pancadas de chuva' };
  if (code >= 85 && code <= 86) return { emoji: '🌨️', desc: 'Pancadas de neve' };
  if (code === 95) return { emoji: '⛈️', desc: 'Trovoadas' };
  if (code === 96 || code === 99) return { emoji: '⛈️', desc: 'Trovoadas com granizo' };
  return { emoji: '🌡️', desc: 'Tempo' };
}

async function fetchWeatherPreview(city: string): Promise<WeatherInfo | null> {
  try {
    const r = await api.get(`/api/signage/weather?city=${encodeURIComponent(city)}`);
    return r.data as WeatherInfo;
  } catch {
    return null;
  }
}

function WeatherSlide({ content }: any) {
  const city = content?.city || 'São Paulo';
  const layout: 'compact' | 'full' | 'animated' =
    content?.layout === 'full' ? 'full'
    : content?.layout === 'animated' ? 'animated'
    : 'compact';
  const [data, setData] = useState<WeatherInfo | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setError(false);
    setData(null);
    fetchWeatherPreview(city).then((d) => {
      if (!active) return;
      if (d) setData(d);
      else setError(true);
    });
    return () => { active = false; };
  }, [city]);

  if (error) {
    return (
      <div className="w-full h-full grid place-items-center bg-blue-700 text-white">
        <div className="text-center">
          <div className="text-7xl">⚠️</div>
          <div className="text-2xl mt-3 opacity-90">Não foi possível carregar a previsão</div>
          <div className="text-xl mt-1 opacity-70">{city}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="w-full h-full grid place-items-center bg-blue-700 text-white">
        <div className="text-2xl opacity-70">Carregando previsão de {city}…</div>
      </div>
    );
  }

  const current = describeWeatherPreview(data.weather_code);
  const showClock = content?.show_clock !== false;

  if (layout === 'animated') {
    return <WeatherAnimatedScene data={data} current={current} showClock={showClock} />;
  }

  if (layout === 'compact') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-blue-700 text-white p-8">
        {showClock && <LiveClockWeb />}
        <div className="flex items-center gap-10">
          <div className="text-[12rem] leading-none">{current.emoji}</div>
          <div className="flex flex-col">
            <div className="text-8xl font-black">{data.temp}°C</div>
            <div className="text-3xl mt-2 opacity-90">{current.desc}</div>
            <div className="text-2xl mt-1 opacity-70">{data.city}</div>
          </div>
        </div>
      </div>
    );
  }

  const upcoming = data.daily.slice(1, 5);
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-blue-700 text-white p-6">
      {showClock && <LiveClockWeb />}
      <div className="text-2xl opacity-85 font-semibold mb-3">{data.city}</div>
      <div className="flex items-center justify-center gap-8 mb-6">
        <div className="text-9xl leading-none">{current.emoji}</div>
        <div>
          <div className="text-7xl font-black leading-none">{data.temp}°C</div>
          <div className="text-2xl mt-2 opacity-90">{current.desc}</div>
        </div>
      </div>
      <div className="flex gap-3 w-full max-w-3xl">
        {upcoming.map((d) => {
          const w = describeWeatherPreview(d.weather_code);
          const dt = new Date(d.date + 'T00:00:00');
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center justify-center bg-white/10 rounded-2xl px-3 py-4">
              <div className="text-xl font-bold opacity-85">{WEEKDAYS_PT_PREVIEW[dt.getDay()]}</div>
              <div className="text-5xl my-1 leading-none">{w.emoji}</div>
              <div className="text-xl font-extrabold">
                {d.temp_max}° <span className="font-medium opacity-70">/ {d.temp_min}°</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

type WeatherScene = 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'storm' | 'fog';

function sceneFromCode(code: number): WeatherScene {
  if (code === 0 || code === 1) return 'sunny';
  if (code === 2 || code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 67) return 'rainy';
  if (code >= 71 && code <= 77) return 'snowy';
  if (code >= 80 && code <= 82) return 'rainy';
  if (code >= 85 && code <= 86) return 'snowy';
  if (code >= 95) return 'storm';
  return 'cloudy';
}

const SCENE_GRADIENT: Record<WeatherScene, string> = {
  sunny:  'linear-gradient(135deg, #FF9A3D 0%, #FFC56B 35%, #4FB6E8 100%)',
  cloudy: 'linear-gradient(135deg, #6B7280 0%, #9CA3AF 50%, #CBD5E1 100%)',
  rainy:  'linear-gradient(135deg, #1E3A5F 0%, #2C5282 50%, #4A6FA5 100%)',
  snowy:  'linear-gradient(135deg, #A8C7E0 0%, #D6E6F2 50%, #F0F7FC 100%)',
  storm:  'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #334155 100%)',
  fog:    'linear-gradient(135deg, #94A3B8 0%, #CBD5E1 50%, #E2E8F0 100%)',
};

function WeatherAnimatedScene({ data, current, showClock }: { data: WeatherInfo; current: { emoji: string; desc: string }; showClock: boolean }) {
  const scene = sceneFromCode(data.weather_code);
  const upcoming = data.daily.slice(1, 5);

  return (
    <div
      className="w-full h-full relative overflow-hidden text-white"
      style={{ backgroundImage: SCENE_GRADIENT[scene] }}
    >
      {/* Camada decorativa atrás do conteúdo */}
      <WeatherSceneEffects scene={scene} />

      {/* Flash de relâmpago em tempestade */}
      {scene === 'storm' && (
        <div
          className="absolute inset-0 pointer-events-none bg-white"
          style={{ animation: 'weather-lightning 6s infinite' }}
        />
      )}

      {/* Conteúdo principal */}
      <div className="absolute inset-0 flex flex-col items-center justify-between px-12 py-10">
        {/* Relógio + Cidade no topo */}
        <div
          className="flex flex-col items-center gap-1"
          style={{ animation: 'weather-fade-up 700ms ease-out both' }}
        >
          {showClock && <LiveClockWeb />}
          <div className="text-3xl font-semibold tracking-wide drop-shadow-lg">
            {data.city}
          </div>
        </div>

        {/* Centro: ícone + temperatura */}
        <div className="flex items-center gap-12">
          <div
            className="text-[14rem] leading-none drop-shadow-2xl"
            style={{ animation: 'weather-bob 4s ease-in-out infinite' }}
          >
            {current.emoji}
          </div>
          <div
            className="flex flex-col"
            style={{ animation: 'weather-fade-up 800ms ease-out 150ms both' }}
          >
            <div className="text-[9rem] font-black leading-none tracking-tighter drop-shadow-2xl">
              {data.temp}°
            </div>
            <div className="text-3xl font-medium opacity-95 -mt-1 drop-shadow">
              {current.desc}
            </div>
          </div>
        </div>

        {/* Previsão dos próximos dias */}
        <div className="flex gap-4 w-full max-w-4xl">
          {upcoming.map((d, idx) => {
            const w = describeWeatherPreview(d.weather_code);
            const dt = new Date(d.date + 'T00:00:00');
            return (
              <div
                key={d.date}
                className="flex-1 flex flex-col items-center justify-center rounded-2xl px-3 py-4 backdrop-blur-sm"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  animation: `weather-fade-up 700ms ease-out ${300 + idx * 120}ms both`,
                }}
              >
                <div className="text-xl font-bold opacity-90">{WEEKDAYS_PT_PREVIEW[dt.getDay()]}</div>
                <div className="text-5xl my-1 leading-none">{w.emoji}</div>
                <div className="text-xl font-extrabold">
                  {d.temp_max}° <span className="font-medium opacity-70">/ {d.temp_min}°</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Máscara horizontal que faz as nuvens entrarem/saírem suaves nas bordas, sem corte abrupto.
const CLOUD_EDGE_FADE: React.CSSProperties = {
  WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 7%, black 93%, transparent 100%)',
  maskImage: 'linear-gradient(to right, transparent 0%, black 7%, black 93%, transparent 100%)',
};

function CloudDriftLayer({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={CLOUD_EDGE_FADE}>
      {children}
    </div>
  );
}

function WeatherSceneEffects({ scene }: { scene: WeatherScene }) {
  if (scene === 'sunny') {
    return (
      <>
        {/* Sol pulsante atrás do conteúdo */}
        <div
          className="absolute rounded-full pointer-events-none"
          style={{
            top: '-10%',
            right: '-10%',
            width: '60vh',
            height: '60vh',
            background: 'radial-gradient(circle, rgba(255,236,160,0.7) 0%, rgba(255,200,80,0.3) 50%, rgba(255,200,80,0) 70%)',
            animation: 'weather-pulse 5s ease-in-out infinite',
          }}
        />
        {/* Raios solares girando */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-30%',
            right: '-30%',
            width: '100vh',
            height: '100vh',
            background: 'conic-gradient(from 0deg, rgba(255,255,255,0.08) 0deg, transparent 12deg, rgba(255,255,255,0.08) 24deg, transparent 36deg, rgba(255,255,255,0.08) 48deg, transparent 60deg, rgba(255,255,255,0.08) 72deg, transparent 84deg, rgba(255,255,255,0.08) 96deg, transparent 108deg)',
            animation: 'weather-spin 60s linear infinite',
          }}
        />
        {/* Nuvens soltas */}
        <CloudDriftLayer>
          <CloudShape top="20%" size={140} opacity={0.6} duration={50} delay={0} />
          <CloudShape top="45%" size={90} opacity={0.4} duration={70} delay={15} />
        </CloudDriftLayer>
      </>
    );
  }
  if (scene === 'cloudy' || scene === 'fog') {
    return (
      <CloudDriftLayer>
        <CloudShape top="12%" size={180} opacity={0.7} duration={45} delay={0} />
        <CloudShape top="25%" size={120} opacity={0.5} duration={60} delay={10} />
        <CloudShape top="60%" size={220} opacity={0.45} duration={55} delay={5} />
        <CloudShape top="78%" size={140} opacity={0.55} duration={70} delay={20} />
      </CloudDriftLayer>
    );
  }
  if (scene === 'rainy') {
    return (
      <>
        <CloudDriftLayer>
          <CloudShape top="8%" size={200} opacity={0.65} duration={70} delay={0} />
          <CloudShape top="20%" size={140} opacity={0.5} duration={90} delay={20} />
        </CloudDriftLayer>
        <RainLayer count={60} />
      </>
    );
  }
  if (scene === 'storm') {
    return (
      <>
        <CloudDriftLayer>
          <CloudShape top="5%" size={240} opacity={0.55} duration={80} delay={0} />
          <CloudShape top="18%" size={160} opacity={0.45} duration={100} delay={15} />
        </CloudDriftLayer>
        <RainLayer count={80} dark />
      </>
    );
  }
  if (scene === 'snowy') {
    return (
      <>
        <CloudDriftLayer>
          <CloudShape top="10%" size={180} opacity={0.55} duration={75} delay={0} />
        </CloudDriftLayer>
        <SnowLayer count={50} />
      </>
    );
  }
  return null;
}

function CloudShape({ top, size, opacity, duration, delay }: { top: string; size: number; opacity: number; duration: number; delay: number }) {
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        top,
        left: 0,
        width: size,
        height: size * 0.4,
        opacity,
        background: 'white',
        borderRadius: '999px',
        boxShadow: `${size * 0.25}px -${size * 0.1}px 0 -${size * 0.05}px white, -${size * 0.2}px -${size * 0.05}px 0 -${size * 0.08}px white, ${size * 0.45}px ${size * 0.05}px 0 -${size * 0.1}px white`,
        filter: 'blur(2px)',
        animation: `weather-cloud-drift ${duration}s linear ${delay}s infinite`,
      }}
    />
  );
}

function RainLayer({ count, dark }: { count: number; dark?: boolean }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: count }).map((_, i) => {
        const left = (i * 97) % 100;
        const dur = 0.6 + ((i * 31) % 9) / 10;
        const delay = ((i * 53) % 30) / 10;
        const height = 14 + ((i * 13) % 14);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: 0,
              left: `${left}%`,
              width: 2,
              height,
              background: dark ? 'rgba(180,200,220,0.7)' : 'rgba(220,235,255,0.85)',
              borderRadius: 2,
              animation: `weather-rain-fall ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

function SnowLayer({ count }: { count: number }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {Array.from({ length: count }).map((_, i) => {
        const left = (i * 89) % 100;
        const dur = 4 + ((i * 7) % 6);
        const delay = ((i * 37) % 50) / 10;
        const size = 4 + ((i * 11) % 8);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              top: 0,
              left: `${left}%`,
              width: size,
              height: size,
              background: 'white',
              borderRadius: '50%',
              boxShadow: '0 0 4px rgba(255,255,255,0.6)',
              animation: `weather-snow-fall ${dur}s linear ${delay}s infinite`,
            }}
          />
        );
      })}
    </div>
  );
}

function HtmlSlide({ content }: any) {
  const html = (content.html ?? '').replace(/\{\{date\}\}/g, new Date().toLocaleDateString('pt-BR')).replace(/\{\{time\}\}/g, new Date().toLocaleTimeString('pt-BR'));
  return (
    <iframe
      srcDoc={`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>html,body{margin:0;padding:0;width:100%;height:100%;background:#000;color:#fff;font-family:system-ui,sans-serif;overflow:hidden}</style></head><body>${html}</body></html>`}
      className="w-full h-full border-0 bg-black"
      sandbox="allow-same-origin"
    />
  );
}

function Unsupported({ type }: { type: string }) {
  return (
    <div className="w-full h-full grid place-items-center bg-slate-800 text-white">
      <div className="text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <div className="text-2xl">Tipo "{type}" não suportado em preview</div>
      </div>
    </div>
  );
}
